import React, { useState, useRef, useEffect } from 'react';
import {
    User,
    Calendar,
    Clock,
    Download,
    Mail,
    X,
    Award,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Target,
    Brain,
    MessageCircle,
    Code,
    Video,
    FileText,
    Phone,
    ExternalLink,
    Camera,
    MapPin,
    Shield,
    PlayCircle,
    Eye,
    Building,
    Star,
    InfoIcon,
    Monitor,
    Globe,
    ZoomIn,
    ZoomOut,
    RotateCw,
    ArrowLeft,
    Server,
    Database,
    Mic,
    MicOff,
    ChevronDown,
    ChevronUp,
    Info
} from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { generatePDFFromHTML } from '../../../api/client';
import { useTranslation } from 'react-i18next';
import useCompanyData from '../../../hooks/useCompanyData';
import { TelemetryChart } from './TelemetryChart';
import { SessionIntegrityPanel } from './SessionIntegrityPanel';
import BenchmarkScorecard from './BenchmarkScorecard';
import { formatQuestionTimeDisplay } from '../../../utils/questionTimeDisplay';

const useReportT = () => {
    const { t } = useTranslation();
    return (key, defaultValue, options = {}) =>
        t(`interviewReport.${key}`, { defaultValue, ...options });
};

function toSafeString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value) || '';
    return String(value);
}

function getNormalizedEvaluationText(rawEvaluation, responseText, score, tr = (k, d) => d) {
    const raw = (rawEvaluation == null
        ? ''
        : typeof rawEvaluation === 'string'
            ? rawEvaluation
            : typeof rawEvaluation === 'object'
                ? (JSON.stringify(rawEvaluation) || '')
                : String(rawEvaluation)).trim();

    if (!raw) return '';

    const response = (responseText || '').trim();
    const responseWordCount = response ? response.split(/\s+/).length : 0;
    const looksLikeShortNonAnswer = score <= 1 && responseWordCount > 0 && responseWordCount <= 3;
    const hasGenericTemplate =
        /does not address the question/i.test(raw) &&
        /no transcription error plausibly accounts/i.test(raw);

    if (looksLikeShortNonAnswer && hasGenericTemplate) {
        return tr(
            'qa.shortResponseEvaluation',
            'The response is too brief and does not address the question.'
        );
    }

    return raw;
}

function computeAlertPercentage(sessionData = {}, feedback = {}) {
    const start = sessionData?.started_at;
    const end = sessionData?.ended_at;
    if (!start || !end) return 0;
    const durationMs = new Date(end) - new Date(start);
    const durationSeconds = durationMs > 0 ? Math.floor(durationMs / 1000) : 0;
    if (durationSeconds === 0) return 0;
    const totalAlerts = (feedback?.detailed_breakdown?.cheating_flags?.length) || 0;
    const alertPercentage = (totalAlerts / durationSeconds) * 100;
    return Number.isFinite(alertPercentage) ? alertPercentage : 0;
}

function getAlertPenalty(alertPercentage = 0, tr = (k, d) => d) {
    if (alertPercentage > 90) return { penalty: 10, label: tr('securityPenalty.penalty10', '10% penalty') };
    if (alertPercentage > 70) return { penalty: 5, label: tr('securityPenalty.penalty5', '5% penalty') };
    if (alertPercentage > 50) return { penalty: 3, label: tr('securityPenalty.penalty3', '3% penalty') };
    return { penalty: 0, label: tr('securityPenalty.penalty0', '0% penalty') };
}

function normalizeComparableId(value) {
    if (value == null || value === '') return null;
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
    return String(value).trim();
}

function matchesQuestionFlag(flag, questionIds) {
    const screenshotQuestionMatch = flag?.screenshot_url?.match(/question_(\d+)/);
    const screenshotQuestionId = screenshotQuestionMatch ? normalizeComparableId(screenshotQuestionMatch[1]) : null;
    const directQuestionId = normalizeComparableId(flag?.question_id);

    return [screenshotQuestionId, directQuestionId].some((id) => id != null && questionIds.has(id));
}

function translateSummaryHeader(header, tr) {
    if (!header) return '';
    const cleanHeader = header.trim().toUpperCase();
    const map = {
        'CULTURAL FIT SCORE': 'culturalFitScore',
        'STRENGTHS': 'strengths',
        'AREAS OF IMPROVEMENT': 'areasOfImprovement',
        'COMMUNICATION SKILLS': 'communicationSkills',
        'CONCLUSION': 'conclusion'
    };
    const key = map[cleanHeader];
    return key ? tr(`summary.${key}`, header) : header;
}

function getAlertTitle(type, tr) {
    const key = `qa.alerts.${type}.title`;
    const map = {
        gaze_down: 'Gaze Down',
        gaze_left: 'Gaze Left',
        gaze_right: 'Gaze Right',
        gaze_up: 'Gaze Up',
        tab_switch: 'Tab Switch',
        multiple_people: 'Multiple People',
        face_not_detected: 'Face Not Detected',
        looking_sideways: 'Looking Sideways',
        phone_detected: 'Phone Detected',
        suspicious_object: 'Suspicious Object',
        audio_mismatch: 'Audio Mismatch'
    };
    const defaultTitle = map[type] || String(type || 'Alert').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
    return tr ? tr(key, defaultTitle) : defaultTitle;
}

function getAlertDescription(type, mainFlag, tr) {
    if (mainFlag?.description) return toSafeString(mainFlag.description);

    const key = `qa.alerts.${type}.desc`;
    const map = {
        gaze_down: 'Gazes down frequently',
        gaze_left: 'Gazes left frequently',
        gaze_right: 'Gazes right frequently',
        gaze_up: 'Suspicious activity detected',
        tab_switch: 'Candidate switched tabs during this question',
        multiple_people: 'Multiple faces detected in frame',
        looking_sideways: 'Looking away from the screen',
        face_not_detected: 'Face moved out of camera view',
        phone_detected: 'Mobile device detected in frame',
        suspicious_object: 'Unidentified objects detected',
        audio_mismatch: 'Background noise or speech detected'
    };
    const defaultDesc = map[type] || 'Suspicious activity detected';
    return tr ? tr(key, defaultDesc) : defaultDesc;
}

function CustomAudioPlayer({ src }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };
        const onDurationChange = () => {
            if (audio.duration && isFinite(audio.duration)) {
                setDuration(audio.duration);
            }
        };
        const onEnded = () => setIsPlaying(false);

        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('durationchange', onDurationChange);
        audio.addEventListener('ended', onEnded);

        // Check if metadata is already loaded
        if (audio.duration && isFinite(audio.duration)) {
            setDuration(audio.duration);
        }

        return () => {
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('durationchange', onDurationChange);
            audio.removeEventListener('ended', onEnded);
        };
    }, [src]);

    const togglePlay = () => {
        const audio = audioRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.pause();
            setIsPlaying(false);
        } else {
            audio.play().then(() => {
                setIsPlaying(true);
            }).catch(err => {
                console.error("Playback failed:", err);
            });
        }
    };

    const toggleMute = () => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;
        const newTime = parseFloat(e.target.value);
        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatAudioTime = (seconds) => {
        if (isNaN(seconds) || seconds === Infinity) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    return (
        <div className="flex items-center gap-3 w-full sm:w-80 md:w-96 py-1 select-none">
            <audio ref={audioRef} src={src} preload="metadata" />

            <style>{`
                .custom-slider::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: #2563eb;
                    cursor: pointer;
                    transition: transform 0.1s ease-in-out;
                }
                .custom-slider::-webkit-slider-thumb:hover {
                    transform: scale(1.2);
                }
                .custom-slider::-moz-range-thumb {
                    width: 12px;
                    height: 12px;
                    border: 0;
                    border-radius: 50%;
                    background: #2563eb;
                    cursor: pointer;
                    transition: transform 0.1s ease-in-out;
                }
                .custom-slider::-moz-range-thumb:hover {
                    transform: scale(1.2);
                }
            `}</style>

            {/* Play/Pause Button */}
            <button
                onClick={togglePlay}
                className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center text-white focus:outline-none transition-colors shadow-sm cursor-pointer shrink-0"
                aria-label={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? (
                    <svg className="w-3.5 h-3.5 text-white fill-current" viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                ) : (
                    <svg className="w-3.5 h-3.5 text-white fill-current ml-0.5" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                )}
            </button>

            {/* Time display */}
            <span className="text-xs font-semibold text-gray-700 shrink-0 min-w-[70px]">
                {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
            </span>

            {/* Progress / Seek bar */}
            <div className="flex-1 flex items-center h-full px-1">
                <input
                    type="range"
                    min="0"
                    max={duration || 1}
                    value={currentTime}
                    onChange={handleSeek}
                    className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer focus:outline-none custom-slider"
                    style={{
                        background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(currentTime / (duration || 1)) * 100}%, #e5e7eb ${(currentTime / (duration || 1)) * 100}%, #e5e7eb 100%)`
                    }}
                />
            </div>

            {/* Speaker Button */}
            <button
                onClick={toggleMute}
                className="focus:outline-none shrink-0 p-1 rounded-full hover:bg-gray-100/50 transition-colors"
                aria-label={isMuted ? "Unmute" : "Mute"}
            >
                {isMuted ? (
                    <svg className="w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                ) : (
                    <svg className="w-5 h-5 text-gray-500 hover:text-gray-700 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M12 18.75V5.25L7.75 9.5H4.5v5h3.25L12 18.75z" />
                    </svg>
                )}
            </button>
        </div>
    );
}

function InfoTooltip({ text }) {
    const [visible, setVisible] = useState(false);
    const tooltipRef = useRef(null);
    const iconRef = useRef(null);

    return (
        <span className="relative inline-flex items-center ml-1">
            <span
                ref={iconRef}
                className="cursor-help"
                onMouseEnter={() => setVisible(true)}
                onMouseLeave={() => setVisible(false)}
                onFocus={() => setVisible(true)}
                onBlur={() => setVisible(false)}
                tabIndex={0}
                role="button"
                aria-label="More information"
            >
                <Info className="w-3 h-3 text-gray-400 hover:text-blue-500 transition-colors" />
            </span>
            {visible && (
                <span
                    ref={tooltipRef}
                    className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-[11px] leading-relaxed text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg w-56 pointer-events-none"
                    style={{ animation: 'tooltipFadeIn 0.15s ease-out' }}
                >
                    {text}
                    <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px">
                        <svg width="12" height="6" viewBox="0 0 12 6" className="fill-white stroke-gray-200">
                            <path d="M0 0L6 6L12 0" strokeWidth="1" />
                        </svg>
                    </span>
                </span>
            )}
            <style>{`
                @keyframes tooltipFadeIn {
                    from { opacity: 0; transform: translate(-50%, 4px); }
                    to   { opacity: 1; transform: translate(-50%, 0); }
                }
            `}</style>
        </span>
    );
}

function TechnicalDetailsSection({ metadata }) {
    const [expanded, setExpanded] = useState(false);

    if (!metadata) return null;

    const noiseLevel = Math.round((metadata.background_noise_level || 0) * 100);
    const speakerCount = metadata.speaker_count ?? (metadata.multi_speaker ? '2+' : '1');
    const silenceDuration = metadata.silence_duration != null
        ? `${parseFloat(metadata.silence_duration).toFixed(1)} sec`
        : '—';
    const audioDuration = metadata.audio_duration != null
        ? `${parseFloat(metadata.audio_duration).toFixed(1)} sec`
        : '—';
    const sampleRate = metadata.sample_rate
        ? `${(metadata.sample_rate / 1000).toFixed(0)} kHz`
        : '16 kHz';
    const avgVolume = metadata.average_volume != null
        ? parseFloat(metadata.average_volume).toFixed(2)
        : '—';

    const rows = [
        { label: 'Noise Level', value: `${noiseLevel}%`, hint: noiseLevel > 60 ? 'High' : noiseLevel > 40 ? 'Moderate' : 'Good' },
        { label: 'Average Volume', value: avgVolume },
        { label: 'System Audio Detected', value: metadata.system_audio ? 'Yes' : 'No' },
        { label: 'Speaker Count', value: String(speakerCount) },
        { label: 'Silence Duration', value: silenceDuration },
        { label: 'Audio Duration', value: audioDuration },
        { label: 'Sample Rate', value: sampleRate },
    ];

    return (
        <div className="mt-3">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-blue-600 transition-colors cursor-pointer select-none group"
            >
                <Database className="w-3 h-3 text-gray-400 group-hover:text-blue-500 transition-colors" />
                <span>{expanded ? 'Hide' : 'Show'} Technical Details</span>
                {expanded
                    ? <ChevronUp className="w-3 h-3" />
                    : <ChevronDown className="w-3 h-3" />
                }
            </button>

            {expanded && (
                <div
                    className="mt-2 bg-gray-50/80 border border-gray-200 rounded-lg overflow-hidden"
                    style={{ animation: 'detailsSlideIn 0.2s ease-out' }}
                >
                    <div className="px-3 py-2 border-b border-gray-200 bg-gray-100/60">
                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Audio Metadata</p>
                    </div>
                    <div className="divide-y divide-gray-100">
                        {rows.map(({ label, value, hint }) => (
                            <div key={label} className="flex items-center justify-between px-3 py-1.5">
                                <span className="text-[11px] text-gray-500">{label}</span>
                                <span className="text-[11px] font-semibold text-gray-800 flex items-center gap-1.5">
                                    {value}
                                    {hint && (
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide ${hint === 'High' ? 'bg-red-100 text-red-600' :
                                            hint === 'Moderate' ? 'bg-amber-100 text-amber-600' :
                                                'bg-green-100 text-green-600'
                                            }`}>
                                            {hint}
                                        </span>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                    <style>{`
                        @keyframes detailsSlideIn {
                            from { opacity: 0; max-height: 0; }
                            to   { opacity: 1; max-height: 500px; }
                        }
                    `}</style>
                </div>
            )}
        </div>
    );
}

function MicValidationSection({ micTestAudio, micTestMetadata }) {
    const tr = useReportT();
    if (!micTestAudio && !micTestMetadata) return null;

    const hasIntegrityIssues = micTestMetadata?.multi_speaker ||
        micTestMetadata?.system_audio ||
        micTestMetadata?.suspicious_silence;

    return (
        <div className={`rounded-xl p-4 border-2 ${!hasIntegrityIssues ? 'border-blue-100 bg-blue-50/30' : 'border-amber-200 bg-amber-50/50'} mb-4`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${!hasIntegrityIssues ? 'bg-blue-600' : 'bg-amber-600'}`}>
                        <Mic className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">{tr('micValidation.title', 'Microphone Validation')}</h3>
                        <p className="text-gray-600 text-xs">{tr('micValidation.subtitle', 'Candidate voice & environment check')}</p>
                    </div>
                </div>

                {micTestAudio && (
                    <div className="w-full sm:w-auto flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Validation Audio</span>
                        <CustomAudioPlayer src={micTestAudio} />
                    </div>
                )}
            </div>

            {(micTestMetadata?.expected_sentence || micTestMetadata?.transcript) && (
                <div className="mt-4 p-3 bg-white/70 border border-gray-150 rounded-lg space-y-3">
                    {micTestMetadata?.expected_sentence && (
                        <div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Expected Sentence</span>
                            <p className="text-xs text-gray-700 italic mt-0.5">"{micTestMetadata.expected_sentence}"</p>
                        </div>
                    )}
                    {micTestMetadata?.transcript && (
                        <div className="border-t border-gray-100 pt-2">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Transcribed Answer</span>
                            <p className="text-xs text-gray-900 font-semibold mt-0.5">"{micTestMetadata.transcript}"</p>
                        </div>
                    )}
                </div>
            )}

            {micTestMetadata && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className={`p-2.5 rounded-lg border ${micTestMetadata.multi_speaker ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                        <div className="flex items-center gap-0.5">
                            <p className="text-[10px] text-gray-500 font-bold uppercase">Multi-Speaker</p>
                            <InfoTooltip text="Detects whether more than one speaker is present in the audio. 'Clear' means only one voice was identified." />
                        </div>
                        <p className={`text-xs font-bold mt-1 ${micTestMetadata.multi_speaker ? 'text-red-600' : 'text-green-600'}`}>
                            {micTestMetadata.multi_speaker ? 'Detected' : 'Clear'}
                        </p>
                    </div>
                    <div className={`p-2.5 rounded-lg border ${micTestMetadata.suspicious_silence ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-100'}`}>
                        <div className="flex items-center gap-0.5">
                            <p className="text-[10px] text-gray-500 font-bold uppercase">Silence Check</p>
                            <InfoTooltip text="Checks for long silent periods or missing speech in the recording. 'Normal' means the candidate spoke during the validation." />
                        </div>
                        <p className={`text-xs font-bold mt-1 ${micTestMetadata.suspicious_silence ? 'text-amber-600' : 'text-green-600'}`}>
                            {micTestMetadata.suspicious_silence ? 'Suspicious' : 'Normal'}
                        </p>
                    </div>
                    <div className="p-2.5 rounded-lg border bg-white border-gray-100">
                        <div className="flex items-center gap-0.5">
                            <p className="text-[10px] text-gray-500 font-bold uppercase">Noise Level</p>
                            <InfoTooltip text="Background/environment noise detected during microphone validation. Lower values indicate a quieter environment. Recommended: below 40%." />
                        </div>
                        <p className={`text-xs font-bold mt-1 ${(micTestMetadata.background_noise_level || 0) > 0.6 ? 'text-red-600' :
                            (micTestMetadata.background_noise_level || 0) > 0.4 ? 'text-amber-600' : 'text-blue-600'
                            }`}>
                            {Math.round((micTestMetadata.background_noise_level || 0) * 100)}%
                        </p>
                    </div>
                    <div className={`p-2.5 rounded-lg border ${micTestMetadata.system_audio ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                        <div className="flex items-center gap-0.5">
                            <p className="text-[10px] text-gray-500 font-bold uppercase">System Audio</p>
                            <InfoTooltip text="Detects whether screen/system audio (music, videos, meeting audio, etc.) was captured during validation. 'None' means no system-generated audio detected." />
                        </div>
                        <p className={`text-xs font-bold mt-1 ${micTestMetadata.system_audio ? 'text-red-600' : 'text-green-600'}`}>
                            {micTestMetadata.system_audio ? 'Detected' : 'None'}
                        </p>
                    </div>
                </div>
            )}

        </div>
    );
}

function SecurityExplanation({ sessionData, feedback }) {
    const tr = useReportT();
    const alertPercentage = computeAlertPercentage(sessionData, feedback);
    if (alertPercentage < 50) return null;
    return (
        <div className="mb-2">
            <div className="flex items-center mb-1">
                <InfoIcon className="w-3 h-3 mr-2" />
                <span className="text-xs text-gray-500">{tr('securityPenalty.rulesTitle', 'Security penalty rules')}</span>
            </div>
            <div className="text-gray-600 space-y-0.5 text-xs">
                <div>{tr('securityPenalty.rule90', '• If Security Alert % > 90 → 10% penalty')}</div>
                <div>{tr('securityPenalty.rule70', '• If Security Alert % > 70 → 5% penalty')}</div>
                <div>{tr('securityPenalty.rule50', '• If Security Alert % > 50 → 3% penalty')}</div>
                <div>{tr('securityPenalty.ruleDefault', '• Otherwise → 0% penalty')}</div>
            </div>
        </div>
    );
}

const ProfessionalScoreCircle = ({
    score,
    size = 110,
    strokeWidth = 7
}) => {
    const tr = useReportT();
    const [animatedScore, setAnimatedScore] = useState(0);
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    useEffect(() => {
        const t = setTimeout(() => setAnimatedScore(score || 0), 400);
        return () => clearTimeout(t);
    }, [score]);
    const strokeDashoffset = circumference - (animatedScore / 100) * circumference;
    const getScoreColor = (s) => {
        if (s >= 80) return '#059669';
        if (s >= 60) return '#0284c7';
        if (s >= 40) return '#ea580c';
        return '#dc2626';
    };
    const getScoreLabel = (s) => {
        if (s >= 80) return tr('scoreLabels.excellent', 'Excellent');
        if (s >= 60) return tr('scoreLabels.good', 'Good');
        if (s >= 40) return tr('scoreLabels.fair', 'Fair');
        return tr('scoreLabels.needsImprovement', 'Needs Improvement');
    };
    const scoreColor = getScoreColor(score || 0);
    return (
        <div className="inline-flex flex-col items-center">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={radius} stroke="#f1f5f9" strokeWidth={strokeWidth} fill="transparent" />
                    <circle cx={size / 2} cy={size / 2} r={radius} stroke={scoreColor} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} strokeLinecap="round" fill="transparent" className="transition-all duration-700" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="font-bold text-2xl" style={{ color: scoreColor }}>{Math.round(score || 0)}</span>
                    <span className="text-[10px] text-gray-400 font-medium tracking-wide uppercase mt-0.5">{tr('labels.percentile', 'Percentile')}</span>
                </div>
            </div>
            <div className="mt-2 px-3 py-0.5 rounded-full font-semibold text-xs tracking-wide whitespace-nowrap text-center" style={{ backgroundColor: `${scoreColor}15`, color: scoreColor }}>
                {getScoreLabel(score || 0)}
            </div>
        </div>
    );
};

function ScoreComparisonGuide({
    effectiveScore,
    totalScore,
    questionCount,
    minimumQuestions = 10,
    sessionData,
    feedback
}) {
    const tr = useReportT();
    const effVal = effectiveScore != null ? Math.round(effectiveScore) : 0;
    const totVal = totalScore != null ? Math.round(totalScore) : null;
    const isBelowRecommended = questionCount != null && questionCount < minimumQuestions;

    return (
        <div>
            {/* Split layout: Score Circle on left, Score breakdown cards on right */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                {/* Score Circle Gauge */}
                <div className="flex flex-col items-center justify-center flex-shrink-0 sm:w-36 pt-0.5">
                    <ProfessionalScoreCircle score={effectiveScore || 0} size={105} strokeWidth={6.5} />
                </div>

                {/* Score Breakdown Cards */}
                <div className="flex-1 w-full space-y-2.5">
                    {/* Effective Score Card */}
                    <div className="bg-emerald-50/70 border border-emerald-200/90 rounded-lg p-2.5 transition-all">
                        <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
                                <Award className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                <span className="text-xs font-bold text-gray-900 whitespace-nowrap">
                                    {tr('scoringGuide.effectiveTitle', 'Effective Score')}
                                </span>
                                <span className="bg-emerald-100 text-emerald-800 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                                    {tr('scoringGuide.primaryTag', 'Use for Ranking')}
                                </span>
                            </div>
                            <span className="text-base font-bold text-emerald-700">{effVal}%</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 leading-snug">
                            {tr('scoringGuide.effectiveDesc', 'Overall score across the entire interview. Use this to compare and rank candidates fairly.')}
                        </p>
                    </div>

                    {/* Total Score Card */}
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 transition-all">
                        <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap">
                                <Target className="w-4 h-4 text-slate-600 flex-shrink-0" />
                                <span className="text-xs font-bold text-gray-900 whitespace-nowrap">
                                    {tr('scoringGuide.totalTitle', 'Total Score')}
                                </span>
                                <span className="bg-slate-200 text-slate-700 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
                                    {tr('scoringGuide.diagnosticTag', 'For Reference')}
                                </span>
                            </div>
                            <span className="text-base font-bold text-slate-700">{totVal != null ? `${totVal}%` : '—'}</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 leading-snug">
                            {tr('scoringGuide.totalDesc', 'Average score based only on answered questions. Measures answer quality without penalizing for unreached questions.')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Context notice when below recommended question count */}
            {isBelowRecommended && (
                <div className="mt-2.5 flex items-center gap-2 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                    <span>
                        {tr('scoringGuide.belowRecommended', 'Candidate answered {{count}} of {{min}} recommended questions. Effective score is scaled to prevent short-interview score distortion.', { count: questionCount, min: minimumQuestions })}
                    </span>
                </div>
            )}

            <SecurityExplanation sessionData={sessionData} feedback={feedback} />
        </div>
    );
}

function ScoreCalculationExplainer({
    effectiveScore,
    totalScore,
    questionCount,
    minimumQuestions = 10,
    sessionData,
    feedback
}) {
    const tr = useReportT();
    const [showFormulas, setShowFormulas] = useState(false);
    const effVal = effectiveScore != null ? Math.round(effectiveScore) : 0;
    const totVal = totalScore != null ? Math.round(totalScore) : null;

    // Derived numbers for live calculation display
    const qaPairs = feedback?.detailed_breakdown?.qa_pairs || [];
    let rawPoints = 0;
    if (qaPairs.length > 0) {
        rawPoints = qaPairs.reduce((acc, qa) => acc + (Number(qa.score) || 0), 0);
    } else if (totVal != null && questionCount > 0) {
        rawPoints = (totVal / 100) * (questionCount * 10);
    }
    rawPoints = Math.round(rawPoints * 10) / 10;

    const alertPct = computeAlertPercentage(sessionData, feedback);
    const securityPenaltyPct = alertPct > 90 ? 10 : alertPct > 70 ? 5 : alertPct > 50 ? 3 : 0;
    const securityDeduction = securityPenaltyPct > 0 ? Math.round((rawPoints * securityPenaltyPct / 100) * 10) / 10 : 0;
    const netPoints = Math.round(Math.max(0, rawPoints - securityDeduction) * 10) / 10;

    const benchmarkQuestions = Math.max(questionCount || 0, minimumQuestions || 10);
    const effMaxPoints = benchmarkQuestions * 10;
    const totalMaxPoints = (questionCount || 0) * 10;

    return (
        <div className="mt-3">
            {/* Recruiter guideline & toggle - full width, outside the grid */}
            <div className="flex items-center justify-between text-xs sm:text-sm mb-1.5">
                <span className="text-gray-600 font-medium">
                    {tr('scoringGuide.recruiterRule', 'Tip: Always use Effective Score for ranking candidates.')}
                </span>
                <button
                    type="button"
                    onClick={() => setShowFormulas(!showFormulas)}
                    className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-semibold transition-colors text-xs sm:text-sm no-print"
                >
                    <span>{showFormulas ? tr('scoringGuide.hideFormulas', 'Hide Calculation') : tr('scoringGuide.showFormulas', 'How is this calculated?')}</span>
                    {showFormulas ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
            </div>

            {/* Expandable: Non-tech-friendly score walkthrough (always included in print/PDF) */}
            <div className={`mt-2 rounded-xl border border-gray-200 bg-white overflow-hidden shadow-xs score-calculation-panel ${showFormulas ? 'block' : 'hidden print:block'}`}>
                {/* Section header */}
                <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        {tr('scoringGuide.howWeScore', 'How We Calculate This Candidate\'s Score')}
                    </h4>
                </div>

                <div className="p-4 sm:p-5 space-y-5">
                    {/* Step 1: Raw performance */}
                    <div>
                        <div className="flex items-center gap-2.5 mb-2">
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex-shrink-0">1</span>
                            <span className="text-sm font-bold text-gray-900">{tr('scoringGuide.step1Title', 'Interview Performance')}</span>
                        </div>
                        <div className="ml-8 bg-blue-50/60 border border-blue-100 rounded-lg p-4">
                            <div className="grid grid-cols-3 gap-3 text-center score-calc-step1-grid">
                                    <div>
                                        <div className="text-2xl font-bold text-blue-700">{questionCount || 0}</div>
                                        <div className="text-xs text-gray-600 font-medium mt-0.5">{tr('scoringGuide.questionsAnswered', 'Questions Answered')}</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-blue-700">{rawPoints}</div>
                                        <div className="text-xs text-gray-600 font-medium mt-0.5">{tr('scoringGuide.pointsEarned', 'Points Earned')}</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-blue-700">{totalMaxPoints}</div>
                                        <div className="text-xs text-gray-600 font-medium mt-0.5">{tr('scoringGuide.maxPossible', 'Max Possible')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Step 2: Security check */}
                        <div>
                            <div className="flex items-center gap-2.5 mb-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-100 text-orange-700 text-xs font-bold flex-shrink-0">2</span>
                                <span className="text-sm font-bold text-gray-900">{tr('scoringGuide.step2Title', 'Security Check')}</span>
                            </div>
                            <div className="ml-8 bg-orange-50/60 border border-orange-100 rounded-lg p-4 space-y-2">
                                <div className="flex items-center justify-between text-xs sm:text-sm">
                                    <span className="text-gray-700">{tr('scoringGuide.securityAlerts', 'Security alerts detected')}:</span>
                                    <span className="font-bold text-gray-900">{feedback?.detailed_breakdown?.cheating_flags?.length || 0}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs sm:text-sm">
                                    <span className="text-gray-700">{tr('scoringGuide.penaltyApplied', 'Penalty applied')}:</span>
                                    <span className={`font-bold ${securityPenaltyPct > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {securityPenaltyPct > 0 ? `${securityPenaltyPct}% (−${securityDeduction} pts)` : tr('scoringGuide.noPenalty', 'None')}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs sm:text-sm pt-2 border-t border-orange-200 font-semibold text-gray-800">
                                    <span>{tr('scoringGuide.netPoints', 'Net points after penalty')}:</span>
                                    <span className="font-bold text-gray-900">{netPoints} / {totalMaxPoints}</span>
                                </div>
                            </div>
                        </div>

                        {/* Step 3: Final scores side by side */}
                        <div>
                            <div className="flex items-center gap-2.5 mb-2">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex-shrink-0">3</span>
                                <span className="text-sm font-bold text-gray-900">{tr('scoringGuide.step3Title', 'Final Scores')}</span>
                            </div>
                            <div className="ml-8 grid grid-cols-1 sm:grid-cols-2 gap-4 score-calc-step3-grid">
                                {/* Effective Score walkthrough */}
                                <div className="border border-emerald-200 rounded-lg overflow-hidden">
                                    <div className="bg-emerald-50 px-3.5 py-2 flex items-center justify-between border-b border-emerald-200">
                                        <span className="text-xs sm:text-sm font-bold text-emerald-800 flex items-center gap-1.5">
                                            <Award className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {tr('scoringGuide.effectiveTitle', 'Effective Score')}
                                        </span>
                                        <span className="text-base font-extrabold text-emerald-700">{effVal}%</span>
                                    </div>
                                    <div className="p-3.5 space-y-2 text-xs text-gray-700">
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">
                                                {tr('scoringGuide.effStep1', 'Start with {{net}} net points', { net: netPoints })}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">
                                                {tr('scoringGuide.effStep2', 'Divide by {{max}} (benchmark: {{bench}} questions × 10)', { max: effMaxPoints, bench: benchmarkQuestions })}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">{tr('scoringGuide.effStep3', 'Multiply by 100 to get percentage')}</span>
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-emerald-100 font-mono text-xs font-medium text-emerald-800 bg-emerald-50/70 rounded-md px-2.5 py-1.5 text-center border border-emerald-100">
                                            {netPoints} ÷ {effMaxPoints} × 100 = <strong>{effVal}%</strong>
                                        </div>
                                    </div>
                                </div>

                                {/* Total Score walkthrough */}
                                <div className="border border-slate-200 rounded-lg overflow-hidden">
                                    <div className="bg-slate-50 px-3.5 py-2 flex items-center justify-between border-b border-slate-200">
                                        <span className="text-xs sm:text-sm font-bold text-slate-800 flex items-center gap-1.5">
                                            <Target className="w-4 h-4 text-slate-600 flex-shrink-0" /> {tr('scoringGuide.totalTitle', 'Total Score')}
                                        </span>
                                        <span className="text-base font-extrabold text-slate-700">{totVal != null ? `${totVal}%` : '—'}</span>
                                    </div>
                                    <div className="p-3.5 space-y-2 text-xs text-gray-700">
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">
                                                {tr('scoringGuide.totStep1', 'Start with {{raw}} raw points', { raw: rawPoints })}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">
                                                {tr('scoringGuide.totStep2', 'Divide by {{max}} ({{count}} answered × 10)', { max: totalMaxPoints, count: questionCount || 0 })}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-gray-400 font-mono text-sm">→</span>
                                            <span className="leading-relaxed">{tr('scoringGuide.totStep3', 'No penalty for unanswered questions')}</span>
                                        </div>
                                        <div className="mt-2 pt-2 border-t border-slate-100 font-mono text-xs font-medium text-slate-800 bg-slate-50 rounded-md px-2.5 py-1.5 text-center border border-slate-200">
                                            {rawPoints} ÷ {totalMaxPoints} × 100 = <strong>{totVal != null ? `${totVal}%` : '—'}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Why are they different? */}
                        {effVal !== (totVal ?? 0) && (
                            <div className="ml-8 bg-amber-50/80 border border-amber-200 rounded-lg p-3.5 text-xs sm:text-sm text-amber-900 leading-relaxed">
                                <span className="font-bold">{tr('scoringGuide.whyDifferentTitle', 'Why are these scores different?')}</span>
                                <span className="ml-1.5">
                                    {questionCount < minimumQuestions
                                        ? tr('scoringGuide.whyDifferentFewer', 'The candidate answered fewer questions ({{count}}) than the {{min}}-question benchmark. The Effective Score uses the benchmark as the base, which produces a lower percentage — ensuring candidates who answer more questions aren\'t disadvantaged.', { count: questionCount, min: minimumQuestions })
                                        : securityPenaltyPct > 0
                                            ? tr('scoringGuide.whyDifferentSecurity', 'A {{pct}}% security penalty was applied because of detected irregularities. The Total Score doesn\'t include this deduction.', { pct: securityPenaltyPct })
                                            : tr('scoringGuide.whyDifferentOther', 'The Effective Score accounts for the overall interview benchmark and security compliance, while Total Score only reflects raw answer quality.')
                                    }
                                </span>
                            </div>
                        )}
                    </div>
                </div>
        </div>
    );
}

const StarRating = ({ score, maxScore = 10 }) => {
    const starScore = (score / maxScore) * 5;
    const fullStars = Math.floor(starScore);
    const hasHalfStar = starScore % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    const starColor = 'text-yellow-400';
    return (
        <div className="flex items-center space-x-0.5">
            {[...Array(fullStars)].map((_, i) => <Star key={`f-${i}`} className={`w-4 h-4 ${starColor} fill-current`} />)}
            {hasHalfStar && (
                <div className="relative">
                    <Star className="w-4 h-4 text-gray-300 fill-current" />
                    <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}><Star className={`w-4 h-4 ${starColor} fill-current`} /></div>
                </div>
            )}
            {[...Array(emptyStars)].map((_, i) => <Star key={`e-${i}`} className="w-4 h-4 text-gray-300 fill-current" />)}
        </div>
    );
};

const ProfessionalSkillChart = ({ categories }) => {
    const tr = useReportT();
    const skills = Object.entries(categories || {});
    if (skills.length === 0) return <div className="text-center text-gray-500 py-4 text-sm">{tr('skills.noSkills', 'No skill assessments available')}</div>;

    // Support both old format {skill: score} and new format {skill: {score, summary}}
    const getScore = (val) => (val !== null && typeof val === 'object' ? val.score ?? 0 : val ?? 0);
    const getSummary = (val) => (val !== null && typeof val === 'object' ? val.summary || '' : '');

    const getBg = (score) => {
        if (score >= 8) return 'bg-emerald-50 border-emerald-200';
        if (score >= 6) return 'bg-sky-50 border-sky-200';
        if (score >= 4) return 'bg-orange-50 border-orange-200';
        return 'bg-red-50 border-red-200';
    };
    return (
        <div className="space-y-2">
            {skills.map(([skill, val]) => {
                const score = getScore(val);
                const summary = getSummary(val);
                const translatedSummary = summary
                    ? summary
                        .replace(/Assessment Focus:/g, tr('skills.assessmentFocus', 'Assessment Focus') + ':')
                        .replace(/Performance Insight:/g, tr('skills.performanceInsight', 'Performance Insight') + ':')
                    : '';

                const displayScore = Number.isFinite(score) ? Math.round((score / 2) * 10) / 10 : 0;
                return (
                    <div key={skill} className={`border rounded-lg p-3 ${getBg(score)}`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-900 text-sm capitalize">{skill}</span>
                                <StarRating score={score} />
                            </div>
                            <span className="font-bold text-gray-900">{displayScore}/5</span>
                        </div>
                        <p
                            className="mt-1.5 text-xs text-gray-600 leading-normal skill-summary-text"
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'pre-wrap',
                                cursor: 'default'
                            }}
                            title={translatedSummary}
                        >
                            {translatedSummary}
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

const MCQOptionsDisplay = ({ mcqData, isCorrect }) => {
    const tr = useReportT();
    if (!mcqData?.choices) return null;
    const { choices, selected_choice_ids = [] } = mcqData;
    const getStyle = (choice) => {
        const sel = selected_choice_ids.includes(choice.id);
        const correct = choice.is_correct;
        if (sel && correct) return 'bg-green-50 border-green-300 text-green-800';
        if (sel && !correct) return 'bg-red-50 border-red-300 text-red-800';
        if (!sel && correct) return 'bg-blue-50 border-blue-300 text-blue-800';
        return 'bg-gray-50 border-gray-200 text-gray-700';
    };
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs text-gray-600">
                <MessageCircle className="w-3 h-3" />
                <span>{tr('qa.mcq.optionsTitle', 'Multiple Choice Options')}</span>
                <span className={`px-1.5 py-0.5 rounded ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{isCorrect ? tr('qa.mcq.correct', 'Correct') : tr('qa.mcq.incorrect', 'Incorrect')}</span>
            </div>
            <div className="grid gap-2">
                {choices.map((choice, i) => {
                    const isSelected = selected_choice_ids.includes(choice.id);
                    return (
                        <div key={choice.id} className={`border rounded p-2 text-sm ${getStyle(choice)}`}>
                            <div className="flex items-center gap-2">
                                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                    {isSelected && choice.is_correct && <CheckCircle className="w-4 h-4 text-green-600" />}
                                    {isSelected && !choice.is_correct && <XCircle className="w-4 h-4 text-red-600" />}
                                </div>
                                <span className="text-gray-600 text-xs font-medium whitespace-nowrap">{tr('qa.mcq.optionLabel', 'Option')} {String.fromCharCode(65 + i)}</span>
                                <p className="text-gray-900 flex-1">{choice.text}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

function parseQuestionText(questionText) {
    if (!questionText) return '';
    if (typeof questionText === 'object') {
        if (questionText.text) return String(questionText.text);
        if (questionText.nameOfQuestion) return String(questionText.nameOfQuestion);
        if (questionText.question) return String(questionText.question);
        try { return JSON.stringify(questionText); } catch { return 'Invalid'; }
    }
    if (typeof questionText !== 'string') return String(questionText);
    try {
        const parsed = JSON.parse(questionText.replace(/'/g, '"'));
        if (parsed?.nameOfQuestion) return String(parsed.nameOfQuestion);
        if (parsed?.text) return String(parsed.text);
    } catch (_) { /* ignore */ }
    return questionText;
}

function normalizeCodeLanguage(language) {
    if (!language) return 'plaintext';
    const normalized = String(language).trim().toLowerCase();
    const languageMap = {
        py: 'python',
        python3: 'python',
        js: 'javascript',
        node: 'javascript',
        nodejs: 'javascript',
        jsx: 'javascript',
        ts: 'typescript',
        tsx: 'typescript',
        cplusplus: 'cpp',
        'c++': 'cpp',
        c: 'c',
        cs: 'csharp',
        'c#': 'csharp',
        golang: 'go',
        kt: 'kotlin',
        kts: 'kotlin',
        rb: 'ruby',
        rs: 'rust',
        swift5: 'swift',
        scala3: 'scala',
        rlang: 'r',
        yml: 'yaml',
        md: 'markdown',
        ps1: 'powershell',
        shell: 'bash',
        sh: 'bash',
        zsh: 'bash',
        mysql: 'sql',
        postgres: 'sql',
        postgresql: 'sql',
        sqlite: 'sql',
        html5: 'markup',
        xml: 'markup',
        dockerfile: 'docker',
        objectivec: 'objectivec',
        'objective-c': 'objectivec'
    };
    return languageMap[normalized] || normalized;
}

function detectCodeLanguageFromText(codeText) {
    if (typeof codeText !== 'string') return 'plaintext';
    const code = codeText.toLowerCase();
    if (!code.trim()) return 'plaintext';

    const starterMatch = code.match(/\b([a-z+#.-]+)\s+starter\b/i);
    if (starterMatch?.[1]) {
        const hinted = normalizeCodeLanguage(starterMatch[1]);
        if (hinted !== 'plaintext') return hinted;
    }

    if (/^\s*[\[{][\s\S]*[\]}]\s*$/.test(code)) {
        try {
            JSON.parse(codeText);
            return 'json';
        } catch (_) {
            // not valid JSON, continue
        }
    }
    // JSON-like object/array with quoted keys/values (tolerates minor syntax issues from transcripts)
    if (/^\s*[\[{][\s\S]*[\]}]\s*$/.test(code) && /"\s*[\w-]+\s*"\s*:/.test(code)) {
        return 'json';
    }

    if (/#include\s*<|std::|cout\s*<</.test(code)) return 'cpp';
    if (/\bprintf\s*\(|\bscanf\s*\(|#include\s*<stdio\.h>/.test(code)) return 'c';
    if (/\busing\s+system\b|console\.writeline|namespace\s+\w+/.test(code)) return 'csharp';
    if (/\bpublic\s+class\b|system\.out\.println|import\s+java\./.test(code)) return 'java';
    if (/\bfun\s+main\s*\(|\bval\s+\w+|\bvar\s+\w+|\bwhen\s*\(|\bmutablelistof\s*\(/.test(code)) return 'kotlin';
    if (/\bdef\s+\w+\s*\(|\bimport\s+\w+|print\s*\(/.test(code)) return 'python';
    if (/\bputs\s+|end\s*$|^\s*class\s+\w+/m.test(code)) return 'ruby';
    if (/\bfunction\b|console\.log|=>|const\s+\w+|let\s+\w+/.test(code)) return 'javascript';
    if (/\binterface\s+\w+\s*{|type\s+\w+\s*=/.test(code)) return 'typescript';
    if (/\bpackage\s+main\b|\bfunc\s+main\s*\(/.test(code)) return 'go';
    if (/\bfn\s+main\s*\(|let\s+mut\s+\w+/.test(code)) return 'rust';
    if (/\bimport\s+swiftui|func\s+\w+\s*\(|let\s+\w+\s*:\s*\w+/.test(code) && code.includes("print(")) return 'swift';
    if ((/\bobject\s+\w+/.test(code) || /\bextends\s+app\b/.test(code)) && (/\bval\b|\bvar\b|\bprintln\s*\(/.test(code))) return 'scala';
    if ((/\bdata\.frame\b|\blibrary\s*\(/.test(code)) || (/\bfunction\s*\(/.test(code) && /<-/.test(code))) return 'r';
    if (/^#!\/bin\/(ba)?sh/m.test(code) || /\becho\s+["'$]|^\s*export\s+[a-z_]\w*=|^\s*if\s+\[\s+/m.test(code)) return 'bash';
    if (/^#!.*powershell/m.test(code) || /\bwrite-host\b|\bget-childitem\b|\$env:[a-z_]\w*/.test(code)) return 'powershell';
    if (/<\?php|\becho\b|\$[a-z_]\w*/.test(code)) return 'php';
    if (/\bselect\b[\s\S]*\bfrom\b|\bselect\b[\s\S]*\bas\b|\binsert\s+into\b|\bupdate\b[\s\S]*\bset\b|\bdelete\s+from\b|\bcreate\s+table\b/.test(code)) return 'sql';
    if ((/^\s*---\s*$/.test(code) || /:\s*[^\n]+/m.test(code)) && !/^\s*[\[{]/.test(code)) return 'yaml';
    if (/^\s*#\s+.+/m.test(code) || code.includes('```')) return 'markdown';
    if (/<\?xml|<\w+[\s>][\s\S]*<\/\w+>/.test(code)) return 'markup';
    if (/\b<!doctype html>|<html|<div|<span|<script/.test(code)) return 'markup';
    if (/\bbody\s*{|\.[\w-]+\s*{|#[\w-]+\s*{/.test(code)) return 'css';

    return 'plaintext';
}

function getLanguageLabel(language) {
    const lang = normalizeCodeLanguage(language);
    const labelMap = {
        plaintext: 'Code',
        javascript: 'JavaScript',
        typescript: 'TypeScript',
        python: 'Python',
        java: 'Java',
        kotlin: 'Kotlin',
        ruby: 'Ruby',
        rust: 'Rust',
        swift: 'Swift',
        cpp: 'C++',
        c: 'C',
        csharp: 'C#',
        go: 'Go',
        php: 'PHP',
        sql: 'SQL',
        bash: 'Bash',
        powershell: 'PowerShell',
        yaml: 'YAML',
        markdown: 'Markdown',
        docker: 'Dockerfile',
        objectivec: 'Objective-C',
        json: 'JSON',
        scala: 'Scala',
        r: 'R',
        markup: 'HTML',
        css: 'CSS'
    };
    return labelMap[lang] || String(lang).toUpperCase();
}

function looksLikeCode(value) {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;
    const codeSignals = [
        /^\s*[\[{][\s\S]*[\]}]\s*$/m,
        /```[\s\S]*```/,
        /^\s*#\s+\S+/m,
        /`[^`\n]+`/,
        /(?:^|\n)\s{2,}\S/,
        /[{};<>]/,
        /\b(function|const|let|var|return|def|class|import|from|public|private|console\.log|printf|cout|select|insert|puts|println|fn|package|interface|type)\b/i,
        /(?:^|\n)\s*(#include|using\s+\w+|package\s+\w+|<?php|if\s*\(|for\s*\(|while\s*\()/m
    ];
    return codeSignals.some((pattern) => pattern.test(trimmed));
}

function getTranscriptOrAnswerText(qa, sessionQuestion) {
    const parts = [
        qa?.transcript_text,
        sessionQuestion?.transcript_text,
        qa?.answer_text,
        sessionQuestion?.answer_text
    ];
    const found = parts.find((v) => typeof v === 'string' && v.trim());
    return found ? found.trim() : '';
}

function getAnswerTextOnly(qa, sessionQuestion) {
    const parts = [qa?.answer_text, sessionQuestion?.answer_text];
    const found = parts.find((v) => typeof v === 'string' && v.trim());
    return found ? found.trim() : '';
}

function getCodeFieldsForLanguageDetection(qa, sessionQuestion) {
    const codeOnly = [
        qa?.submitted_code,
        qa?.answer_code,
        qa?.code,
        qa?.source_code,
        qa?.answer,
        sessionQuestion?.submitted_code,
        sessionQuestion?.answer_code,
        sessionQuestion?.code,
        sessionQuestion?.source_code
    ];
    const directCode = codeOnly.find((value) => looksLikeCode(value));
    if (directCode) return directCode;
    return codeOnly.find((value) => typeof value === 'string' && value.trim()) || '';
}

function getCodeAnswer(qa, sessionQuestion, isCoding = false) {
    const narrative = isCoding
        ? getAnswerTextOnly(qa, sessionQuestion)
        : getTranscriptOrAnswerText(qa, sessionQuestion);
    if (narrative) return narrative;

    const codeCandidates = [
        qa?.submitted_code,
        qa?.answer_code,
        qa?.code,
        qa?.source_code,
        qa?.answer,
        sessionQuestion?.submitted_code,
        sessionQuestion?.answer_code,
        sessionQuestion?.code,
        sessionQuestion?.source_code
    ];

    const directCode = codeCandidates.find((value) => looksLikeCode(value));
    if (directCode) return directCode;

    if (isCoding) {
        const firstCodeValue = codeCandidates.find((value) => typeof value === 'string' && value.trim());
        return firstCodeValue || '';
    }

    const firstNonEmpty = codeCandidates.find((value) => typeof value === 'string' && value.trim());
    return firstNonEmpty || '';
}

function getQuestionLanguage(qa, sessionQuestion) {
    const explicitLanguage = normalizeCodeLanguage(
        qa?.programming_language ||
        qa?.language ||
        qa?.question?.programming_language ||
        qa?.question?.language ||
        sessionQuestion?.programming_language ||
        sessionQuestion?.language ||
        sessionQuestion?.question?.programming_language ||
        sessionQuestion?.question?.language
    );

    const genericLanguageValues = new Set(['plaintext', 'text', 'txt', 'code', 'answer']);
    if (!genericLanguageValues.has(explicitLanguage)) return explicitLanguage;

    const codeAnswer = getCodeFieldsForLanguageDetection(qa, sessionQuestion);
    return detectCodeLanguageFromText(codeAnswer);
}

const MONO_FONT_STACK = "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, 'Courier New', monospace";

const ReadOnlyCodeAnswer = ({ code, language, title }) => (
    <div
        className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm font-mono"
        style={{ fontFamily: MONO_FONT_STACK }}
    >
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2 min-w-0">
                <Code className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-gray-700">{title}</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700 border border-gray-200">
                    {getLanguageLabel(language)}
                </span>
            </div>
        </div>
        <div
            className="max-w-full overflow-auto font-mono"
            style={{ fontFamily: MONO_FONT_STACK }}
            role="region"
            aria-label={title}
            tabIndex={0}
        >
            <SyntaxHighlighter
                language={language}
                style={oneLight}
                showLineNumbers
                wrapLines
                wrapLongLines={false}
                customStyle={{
                    margin: 0,
                    padding: '16px',
                    minWidth: '100%',
                    background: 'transparent',
                    color: '#1f2937',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    fontFamily: MONO_FONT_STACK
                }}
                codeTagProps={{
                    style: {
                        fontFamily: MONO_FONT_STACK
                    }
                }}
                lineNumberStyle={{
                    minWidth: '3em',
                    paddingRight: '1em',
                    color: '#9ca3af',
                    fontFamily: MONO_FONT_STACK,
                    userSelect: 'none',
                    textAlign: 'right'
                }}
            >
                {code || ''}
            </SyntaxHighlighter>
        </div>
    </div>
);

const ProfessionalQACard = ({ qa, index, sessionData, feedback, setZoomModal, setVideoModal }) => {
    const tr = useReportT();
    const safeString = (v) => (v == null ? '' : typeof v === 'string' ? v : typeof v === 'object' ? (JSON.stringify(v) || '') : String(v));
    let sessionQuestion = sessionData?.questions?.[index];
    if (qa.id) sessionQuestion = qa;
    else if (!sessionQuestion && qa.question_id) sessionQuestion = sessionData?.questions?.find(q => q.question?.id === qa.question_id || q.id === qa.question_id);
    const score = qa.score ?? sessionQuestion?.score ?? 0;
    const getScoreColor = (s) => { if (s >= 8) return 'border-emerald-200 bg-emerald-50'; if (s >= 6) return 'border-sky-200 bg-sky-50'; if (s >= 4) return 'border-orange-200 bg-orange-50'; return 'border-red-200 bg-red-50'; };
    const getScoreIcon = (s) => { if (s >= 8) return <CheckCircle className="w-4 h-4 text-emerald-600" />; if (s >= 6) return <CheckCircle className="w-4 h-4 text-sky-600" />; if (s >= 4) return <AlertTriangle className="w-4 h-4 text-orange-600" />; return <XCircle className="w-4 h-4 text-red-600" />; };
    const getScoreBadge = (s) => { if (s >= 8) return 'bg-emerald-600 text-white'; if (s >= 6) return 'bg-sky-600 text-white'; if (s >= 4) return 'bg-orange-600 text-white'; return 'bg-red-600 text-white'; };
    const plagiarismStatus = sessionQuestion?.plagiarism === 'no' ? { bg: 'bg-green-50 border-green-200', text: tr('qa.originalContent', 'Original Content'), icon: <CheckCircle className="w-3 h-3 text-green-600" /> }
        : sessionQuestion?.plagiarism === 'yes' ? { bg: 'bg-red-50 border-red-200', text: tr('qa.plagiarismDetected', 'Plagiarism Detected'), icon: <XCircle className="w-3 h-3 text-red-600" /> }
            : { bg: 'bg-gray-50 border-gray-200', text: tr('qa.notAnalyzed', 'Not Analyzed'), icon: <AlertTriangle className="w-3 h-3 text-gray-600" /> };
    const questionIds = new Set(
        [sessionQuestion?.id, sessionQuestion?.question?.id, qa?.question_id]
            .map(normalizeComparableId)
            .filter((value) => value != null)
    );
    const questionCheatingFlags = (feedback?.detailed_breakdown?.cheating_flags || []).filter((flag) => {
        if (questionIds.size === 0) return false;
        return matchesQuestionFlag(flag, questionIds);
    });
    const questionVideo = sessionQuestion?.question_video_url;
    const rawQuestionType = qa?.question_type ?? sessionQuestion?.question_type ?? qa?.question?.type ?? sessionQuestion?.question?.type;
    const questionTypeLabel = (() => {
        if (rawQuestionType == null || String(rawQuestionType).trim() === '') return null;
        const key = String(rawQuestionType).toLowerCase().trim();
        const map = {
            mcq: tr('qa.questionTypes.mcq', 'MCQ'),
            coding: tr('qa.questionTypes.coding', 'Coding'),
            essay: tr('qa.questionTypes.essay', 'Essay'),
            short_answer: tr('qa.questionTypes.short_answer', 'Short answer'),
            'short answer': tr('qa.questionTypes.short_answer', 'Short answer'),
            behavioral: tr('qa.questionTypes.behavioral', 'Behavioral'),
            technical: tr('qa.questionTypes.technical', 'Technical')
        };
        if (map[key]) return map[key];
        return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    })();
    const isMcq = qa.question_type === 'mcq' || (typeof (qa.question || sessionQuestion?.question?.text) === 'object' && (qa.question?.type === 'mcq' || sessionQuestion?.question?.type === 'mcq'));
    const isCodingQuestionType = String(rawQuestionType ?? '').trim().toLowerCase() === 'coding';
    const submittedCode = getCodeAnswer(qa, sessionQuestion, true);
    const codeLanguage = getQuestionLanguage(qa, sessionQuestion);
    const timeLimit = qa?.time_limit ?? sessionQuestion?.time_limit ?? qa?.question?.time_limit ?? sessionQuestion?.question?.time_limit;
    const timeSpent = qa?.time_spent ?? sessionQuestion?.time_spent;
    const timerSeconds = qa?.timer_seconds ?? sessionQuestion?.timer_seconds
        ?? qa?.question?.timer_seconds ?? sessionQuestion?.question?.timer_seconds;
    // Coding: Question.time_limit. Theoretical: 2 min recording cap. MCQ: timer_seconds or 90 sec.
    const timeDisplay = formatQuestionTimeDisplay({
        questionType: rawQuestionType,
        timeSpent,
        timeLimit,
        timerSeconds,
        labels: {
            timeTaken: tr('qa.timeTaken', 'Time taken'),
            timeLimit: tr('qa.timeLimit', 'Time limit'),
        },
    });
    const candidateResponseText = getCodeAnswer(qa, sessionQuestion, false);
    const transcriptOrAnswerNarrative = getTranscriptOrAnswerText(qa, sessionQuestion);
    const evaluationText = qa?.evaluation ?? sessionQuestion?.evaluation;
    const [expandedAlerts, setExpandedAlerts] = useState(false);
    const groupedAlerts = (questionCheatingFlags || []).reduce((acc, flag) => {
        const type = flag.type || 'alert';
        if (!acc[type]) acc[type] = [];
        acc[type].push(flag);
        return acc;
    }, {});

    return (
        <div className={`border rounded-lg p-4 ${getScoreColor(score)}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                    <span className="flex-shrink-0 w-8 h-8 rounded bg-gradient-to-r from-cyan-500 to-blue-600 text-white flex items-center justify-center text-sm font-bold">Q{index + 1}</span>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap gap-1.5 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getScoreBadge(score)}`}>{qa.skill_tag || sessionQuestion?.question?.skill_tag}</span>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${plagiarismStatus.bg}`}>{plagiarismStatus.icon}{plagiarismStatus.text}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900 text-sm leading-snug break-words">{parseQuestionText(qa.question || sessionQuestion?.question?.text)}</h4>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {questionTypeLabel && (
                        <span className="inline-flex items-center rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-0.5 text-[11px] font-semibold text-cyan-800" title={tr('qa.questionType', 'Question type')}>
                            {questionTypeLabel}
                        </span>
                    )}
                    {getScoreIcon(score)}
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${getScoreBadge(score)}`}>{Number(score).toFixed(1)}/10</span>
                </div>
            </div>
            <div className="bg-white rounded p-3 mb-3 border border-gray-200">
                {isMcq && qa.mcq_data ? (
                    <>
                        {transcriptOrAnswerNarrative ? (
                            <>
                                <div className="flex items-center gap-1.5 mb-1.5 text-xs text-gray-600">
                                    <MessageCircle className="w-3 h-3" />
                                    <span>{tr('qa.candidateResponse', 'Candidate Response')}</span>
                                </div>
                                <p className="text-gray-800 italic text-sm mb-3">"{transcriptOrAnswerNarrative}"</p>
                            </>
                        ) : null}
                        <MCQOptionsDisplay mcqData={qa.mcq_data} isCorrect={score >= 8 || (qa.mcq_data?.choices || []).some(c => c.is_correct && (qa.mcq_data?.selected_choice_ids || []).includes(c.id))} />
                    </>
                ) : isCodingQuestionType ? (
                    <>
                        <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-600">
                            <Code className="w-3 h-3" />
                            <span>{tr('qa.candidateResponse', 'Candidate Response')}</span>
                        </div>
                        <ReadOnlyCodeAnswer
                            code={submittedCode || tr('qa.noResponse', 'No response provided')}
                            language={codeLanguage}
                            title={tr('qa.submittedCode', 'Submitted Code')}
                        />
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-1.5 mb-1.5 text-xs text-gray-600">
                            <MessageCircle className="w-3 h-3" />
                            <span>{tr('qa.candidateResponse', 'Candidate Response')}</span>
                        </div>
                        <p className="text-gray-800 italic text-sm">"{candidateResponseText || tr('qa.noResponse', 'No response provided')}"</p>
                    </>
                )}
            </div>
            {evaluationText && (
                <div className="bg-indigo-50 border border-indigo-200 rounded p-2.5 mb-3">
                    <div className="flex items-start gap-2">
                        <Brain className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <p className="text-indigo-800 text-xs leading-relaxed">{safeString(evaluationText)}</p>
                    </div>
                </div>
            )}
            {questionCheatingFlags.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2.5 mb-3">
                    <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-red-600 flex-shrink-0" />
                        <span className="font-semibold text-red-900 text-xs">{tr('qa.securityAlerts', 'Security Alerts')} ({questionCheatingFlags.length})</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(groupedAlerts).map(([type, flags]) => {
                            const mainFlag = flags[0];
                            const hasMore = flags.length > 1;
                            const mainTimestamp = mainFlag?.timestamp
                                ? new Date(mainFlag.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })
                                : null;
                            const endTimestamp = mainFlag?.end_timestamp || mainFlag?.ended_at;
                            const hasPreviewImage = Boolean(mainFlag?.screenshot_url);

                            return (
                                <div key={type} className="bg-white border border-red-100 rounded-xl p-3 flex flex-col shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex gap-3 mb-2">
                                        {hasPreviewImage ? (
                                            <div
                                                className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden cursor-pointer flex-shrink-0"
                                                onClick={() => setZoomModal({ isOpen: true, src: resolveMediaUrl(mainFlag.screenshot_url), alt: type, isPdf: false, pdfUrl: null })}
                                            >
                                                <img src={resolveMediaUrl(mainFlag.screenshot_url)} alt="alert" className="w-full h-full object-cover shadow-sm" />
                                            </div>
                                        ) : (
                                            <div className="w-16 h-16 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100">
                                                {type === 'tab_switch'
                                                    ? <Monitor className="w-6 h-6 text-red-300" />
                                                    : <Shield className="w-6 h-6 text-red-300" />}
                                            </div>
                                        )}

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-1 mb-0.5">
                                                <h5 className="font-bold text-gray-900 text-[11px] truncate capitalize leading-tight">
                                                    {getAlertTitle(type, tr)}
                                                </h5>
                                                <span className="bg-red-50 text-red-600 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-red-100 flex-shrink-0">
                                                    x{flags.length}
                                                </span>
                                            </div>
                                            <p className="text-gray-500 text-[10px] leading-tight mb-2">
                                                {getAlertDescription(type, mainFlag, tr)}
                                            </p>

                                            <div className="flex items-center gap-1.5 text-gray-700 text-[10px] font-medium">
                                                <Clock className="w-3 h-3 text-red-400 flex-shrink-0" />
                                                <span className="truncate">
                                                    {mainTimestamp || tr('qa.timeUnavailable', 'Time unavailable')}
                                                </span>
                                            </div>
                                            {type === 'tab_switch' && endTimestamp && (
                                                <div className="mt-1 text-[9px] font-medium text-red-600">
                                                    Until {new Date(endTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {hasMore && (
                                        <div className="pt-2 border-t border-red-50 mt-1">
                                            <button
                                                type="button"
                                                onClick={() => setExpandedAlerts(s => !s)}
                                                className="flex items-center gap-1.5 text-[9px] font-bold text-gray-700 hover:text-red-700 transition-colors uppercase focus:outline-none w-full"
                                            >
                                                {expandedAlerts ? (
                                                    <><span className="text-[8px]">▲</span> {tr('qa.showLess', 'Show less')}</>
                                                ) : (
                                                    <><span className="text-[8px]">▶</span> {tr('qa.moreOccurrences', '+{{count}} more occurrence{{suffix}}', { count: flags.length - 1, suffix: flags.length - 1 === 1 ? '' : 's' })}</>
                                                )}
                                            </button>

                                            {expandedAlerts && (
                                                <div className="mt-2 space-y-1 max-h-24 overflow-y-auto pr-1">
                                                    {flags.slice(1).map((f, idx) => (
                                                        <div key={idx} className="flex items-center justify-between text-[9px] text-gray-600 bg-gray-50/70 px-2 py-1.5 rounded group/item">
                                                            <span className="flex items-center gap-1.5">
                                                                <Clock className="w-2.5 h-2.5 text-gray-400" />
                                                                {new Date(f.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}
                                                            </span>
                                                            {f.screenshot_url && (
                                                                <button
                                                                    onClick={() => setZoomModal({ isOpen: true, src: resolveMediaUrl(f.screenshot_url), alt: type, isPdf: false, pdfUrl: null })}
                                                                    className="text-red-400 hover:text-red-600"
                                                                >
                                                                    <Eye className="w-2.5 h-2.5" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-xs">
                {(() => {
                    const clipUrl = questionVideo || qa?.question_video_url || qa?.video_url;
                    const seekUrl = qa?.session_recording_url;
                    const startSeconds = qa?.clip_start_seconds;
                    const watchUrl = clipUrl || ((startSeconds != null && seekUrl) ? seekUrl : null);
                    if (watchUrl) {
                        // Kept as an anchor so the exported PDF still carries a clickable
                        // recording link; the click handler plays it inline instead.
                        return (
                            <a
                                href={watchUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    if (!setVideoModal) return;
                                    e.preventDefault();
                                    setVideoModal({
                                        isOpen: true,
                                        url: watchUrl,
                                        title: tr('qa.questionClipTitle', 'Question {{number}} Video Clip', { number: index + 1 }),
                                        startSeconds: clipUrl ? undefined : startSeconds,
                                    });
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 font-semibold hover:bg-blue-100 transition-colors border border-blue-200 no-underline"
                            >
                                <PlayCircle className="w-4 h-4 text-blue-600" />
                                <span>{tr('qa.watchResponse', 'Watch Response')}</span>
                            </a>
                        );
                    }
                    if (sessionData?.interview_version === '2.0' || qa?.question_type === 'voice') {
                        return (
                            <span className="text-gray-500 font-medium flex items-center gap-1">
                                <Mic className="w-3 h-3 text-cyan-600" />
                                {tr('qa.liveVoiceTurn', 'Live Voice Turn')}
                            </span>
                        );
                    }
                    return <span className="text-gray-400">{tr('qa.videoUnavailable', 'Video not available')}</span>;
                })()}
                {timeDisplay && (
                    <span className="text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {timeDisplay}
                    </span>
                )}
            </div>
        </div>
    );
};

const ZoomModal = ({ isOpen, onClose, src, alt, isPdf = false, pdfUrl = null }) => {
    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    useEffect(() => { if (isOpen) { setZoom(1); setPosition({ x: 0, y: 0 }); } }, [isOpen]);
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === '+' || e.key === '=') { e.preventDefault(); setZoom(z => Math.min(z + 0.25, 5)); }
            if (e.key === '-') { e.preventDefault(); setZoom(z => Math.max(z - 0.25, 0.5)); }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);
    const handleMouseDown = (e) => { if (zoom > 1) { setIsDragging(true); setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y }); } };
    useEffect(() => {
        if (!isDragging) return;
        const move = (e) => setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
        const up = () => setIsDragging(false);
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
        return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    }, [isDragging, dragStart]);
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/90 z-[999999] flex items-center justify-center p-4" onClick={onClose}>
            <div className="relative w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 z-50 bg-white/20 text-white rounded-full p-2" aria-label="Close"><X className="w-5 h-5" /></button>
                <div className="absolute top-4 left-4 z-50 flex gap-2">
                    <button onClick={e => { e.stopPropagation(); setZoom(z => Math.min(z + 0.25, 5)); }} className="bg-white/20 text-white rounded p-2"><ZoomIn className="w-4 h-4" /></button>
                    <button onClick={e => { e.stopPropagation(); setZoom(z => Math.max(z - 0.25, 0.5)); }} className="bg-white/20 text-white rounded p-2"><ZoomOut className="w-4 h-4" /></button>
                    <button onClick={e => { e.stopPropagation(); setZoom(1); setPosition({ x: 0, y: 0 }); }} className="bg-white/20 text-white rounded p-2"><RotateCw className="w-4 h-4" /></button>
                </div>
                <div className="flex items-center justify-center" style={{ transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)` }} onMouseDown={handleMouseDown}>
                    {isPdf && pdfUrl ? <object data={pdfUrl} type="application/pdf" className="max-w-full max-h-[90vh]" style={{ width: '800px', height: '600px' }}><embed src={pdfUrl} type="application/pdf" /></object> : <img src={src} alt={alt || 'Zoom'} className="max-w-full max-h-[90vh] object-contain" draggable={false} />}
                </div>
            </div>
        </div>
    );
};


const resolveMediaUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/?api\/?$/i, '');
    const base = apiBase || (process.env.REACT_APP_FRONTEND_URL || '').replace(/\/$/, '');
    return base ? `${base}${url.startsWith('/') ? '' : '/'}${url}` : url;
};

export default function ViewResult({
    feedback,
    loadingFeedback,
    feedbackError,
    onClose,
    sessionData,
    emailSent,
    isSendingEmail,
    sendEmail,
    callFrom,
    isPage = false
}) {
    const contentRef = useRef();
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const [aadhaarPdfUrl, setAadhaarPdfUrl] = useState(null);
    const [isAadhaarPdf, setIsAadhaarPdf] = useState(false);
    const [zoomModal, setZoomModal] = useState({ isOpen: false, src: null, alt: '', isPdf: false, pdfUrl: null });
    const [videoModal, setVideoModal] = useState({ isOpen: false, url: null, title: '', startSeconds: undefined });
    const [reportLoaderStep, setReportLoaderStep] = useState(0);
    const [loadCompleteFlash, setLoadCompleteFlash] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadStep, setDownloadStep] = useState('');

    const prevLoadingFeedbackRef = useRef(loadingFeedback);
    const role = localStorage.getItem('role');
    const { t, i18n } = useTranslation();
    const tr = useReportT();
    const { companyLogo } = useCompanyData();
    const reportTitle = tr('title', 'Interview Assessment Report');
    const reportSubtitle = tr('subtitle', 'AI-Powered Performance Analysis');

    useEffect(() => {
        if (!loadingFeedback) {
            setReportLoaderStep(0);
            return undefined;
        }
        setReportLoaderStep(0);
        const id = setInterval(() => {
            setReportLoaderStep((s) => (s + 1) % 3);
        }, 1400);
        return () => clearInterval(id);
    }, [loadingFeedback]);

    useEffect(() => {
        const wasLoading = prevLoadingFeedbackRef.current;
        prevLoadingFeedbackRef.current = loadingFeedback;
        if (wasLoading && !loadingFeedback && feedback && !feedbackError) {
            setLoadCompleteFlash(true);
            const doneMs = 550;
            const t = setTimeout(() => setLoadCompleteFlash(false), doneMs);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [loadingFeedback, feedback, feedbackError]);

    useEffect(() => {
        const aadhaarRaw = feedback?.aadhaar_card || sessionData?.aadhaar_card;
        const url = resolveMediaUrl(aadhaarRaw);
        const looksPdf = /\.pdf(\?|#|$)/i.test((url || '').toLowerCase()) || feedback?.aadhaar_card_content_type === 'application/pdf' || sessionData?.aadhaar_card_content_type === 'application/pdf';
        setIsAadhaarPdf(!!looksPdf);
        if (!url || !looksPdf) { setAadhaarPdfUrl(null); return; }
        let aborted = false;
        (async () => {
            try {
                const res = await fetch(url, { credentials: 'include' });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const blob = await res.blob();
                const objectUrl = URL.createObjectURL(blob);
                if (!aborted) setAadhaarPdfUrl(objectUrl);
            } catch (_) { if (!aborted) setAadhaarPdfUrl(null); }
        })();
        return () => { aborted = true; if (aadhaarPdfUrl) URL.revokeObjectURL(aadhaarPdfUrl); };
    }, [feedback?.aadhaar_card, sessionData?.aadhaar_card, feedback?.aadhaar_card_content_type, sessionData?.aadhaar_card_content_type]);

    const userDetails = JSON.parse(localStorage.getItem('user') || '{}');

    const calculateTime = (date1Data, date2Data) => {
        if (!date1Data || !date2Data) return '—';
        const diffMs = new Date(date2Data) - new Date(date1Data);
        if (!Number.isFinite(diffMs) || diffMs <= 0) return '—';
        const totalSeconds = Math.floor(diffMs / 1000);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
    };

    // Prefer the moment the candidate clicked Start so the reported duration
    // excludes setup/waiting time, matching billing (get_actual_duration_minutes).
    const interviewStartedAt = sessionData?.metadata?.actual_started_at || sessionData?.started_at;

    const extractLocationText = () => {
        const candidates = [feedback?.detailed_breakdown?.verification?.location, feedback?.verification?.location, feedback?.location, sessionData?.verification?.location, sessionData?.location, sessionData?.candidate?.profile?.location];
        const value = candidates.find(Boolean);
        if (!value) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
            if (value.formatted_address) return value.formatted_address;
            const { name, city, state, country, pincode, postal_code, zip } = value;
            return [name, city, state, country].filter(Boolean).join(', ') + (pincode || postal_code || zip ? ' ' + (pincode || postal_code || zip) : '') || '';
        }
        return '';
    };

    const extractCoordinates = () => {
        const candidates = [feedback?.detailed_breakdown?.verification?.location, feedback?.verification?.location, sessionData?.verification?.location, sessionData?.location];
        let value = candidates.find(Boolean);
        if (!value) return null;
        if (typeof value === 'string') { try { value = JSON.parse(value); } catch { return null; } }
        if (typeof value === 'object') {
            const lat = value.latitude ?? value.lat;
            const lng = value.longitude ?? value.lng;
            if (lat != null && lng != null) return { latitude: Number(lat), longitude: Number(lng) };
        }
        return null;
    };

    const extractBrowserText = () => {
        let info = sessionData?.metadata?.technical_metadata?.device_info
            || sessionData?.device_info
            || feedback?.device_info;
        if (!info) return sessionData?.browser || '';
        if (typeof info === 'string') {
            try {
                info = JSON.parse(info);
            } catch {
                return info;
            }
        }
        if (typeof info === 'object' && info !== null) {
            const browserName = info.browser || '';
            const osName = info.os || '';
            const res = info.resolution || '';
            const parts = [browserName, osName].filter(Boolean);
            if (parts.length > 0) {
                return parts.join(' on ') + (res ? ` (${res})` : '');
            }
            return '';
        }
        return sessionData?.browser || '';
    };

    const generateFullyRenderedHTML = async () => {
        const reportArea = document.getElementById('report-print-area');
        if (!reportArea) throw new Error('Report content not found');
        const card = reportArea.querySelector('.report-print-card');
        if (!card) throw new Error('Report card not found');

        // Deep clone the card content
        const clone = card.cloneNode(true);
        // Remove buttons, modals, overlays
        clone.querySelectorAll('.no-print').forEach(el => el.remove());

        // Ensure score calculation explainer is visible in PDF
        clone.querySelectorAll('.score-calculation-panel').forEach(el => {
            el.classList.remove('hidden');
            el.style.display = 'block';
        });

        // Convert all images to base64
        const origImgs = card.querySelectorAll('img');
        const cloneImgs = clone.querySelectorAll('img');
        await Promise.all(
            Array.from(origImgs).map(async (img, i) => {
                if (!img.src || img.src.startsWith('data:') || !cloneImgs[i]) return;
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth || img.width || 200;
                    canvas.height = img.naturalHeight || img.height || 200;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    cloneImgs[i].src = canvas.toDataURL('image/png');
                } catch {
                    try {
                        const res = await fetch(img.src, { credentials: 'include' });
                        const blob = await res.blob();
                        const base64 = await new Promise(resolve => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        cloneImgs[i].src = base64;
                    } catch (e2) {
                        console.warn('Could not embed image:', img.src, e2);
                    }
                }
            })
        );

        const cssText = Array.from(document.styleSheets)
            .map(sheet => {
                try {
                    return Array.from(sheet.cssRules || sheet.rules).map(r => r.cssText).join('\n');
                } catch {
                    return sheet.href ? `@import url("${sheet.href}");` : '';
                }
            })
            .filter(Boolean)
            .join('\n');

        const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
            .map(l => l.outerHTML)
            .join('\n');

        const pdfTitle = `${sessionData?.candidate?.firstName || ''}_${sessionData?.candidate?.lastName || ''}_${sessionData?.job_description_title || ''}`.replace(/^_+|_+$/g, '').replace(/[<>:"/\\|?*]/gi, '_');

        const fullHtml = `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<title>${pdfTitle}</title>
${linkTags}
<style>
${cssText}
@page { margin: 0; size: A4 portrait; }
*, *::before, *::after {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}
body {
    margin: 0 !important;
    padding: 10mm 10mm 20px 10mm !important;
    background: white !important;
    font-family: 'Poppins', sans-serif !important;
    overflow: visible !important;
}
.report-print-card {
    max-width: 100% !important; 
    width: 100% !important; height: auto !important;
    overflow: visible !important; border-radius: 0 !important;
    box-shadow: none !important; border: none !important;
}
/* Force desktop-like grid layout in PDF */
.report-print-card .grid {
    display: grid !important;
}
/* Candidate Info Section: Narrow left column for images */
.report-print-card .CandidateInformationSection {
    grid-template-columns: 300px 1fr !important;
    display: grid !important;
    gap: 1.5rem !important;
}
/* Performance Dashboard Section: Main score left, stats right */
.report-print-card .PerformanceDashboardSection {
    grid-template-columns: 1.8fr 1fr !important;
    display: grid !important;
    gap: 1rem !important;
}
/* Force Gradient background for header and badges */
.report-print-card .bg-gradient-to-r.from-cyan-500.to-blue-600 {
    background: linear-gradient(135deg, #06b6d4 0%,rgb(122, 25, 82) 100%) !important;
    -webkit-print-color-adjust: exact !important;
}
.report-print-card .bg-gradient-to-r.from-blue-600.to-indigo-600 {
    background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%) !important;
    -webkit-print-color-adjust: exact !important;
}

/* Ensure images in the ID section stay side-by-side */
.report-print-card .grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 0.75rem !important;
}

.report-print-scroll {
    overflow: visible !important; max-height: none !important;
    height: auto !important; display: block !important;
}
.pdf-page-break-before {
    page-break-before: always !important;
    break-before: page !important;
}
.rounded-xl, .rounded-lg {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
}
.report-print-card .skill-summary-text {
    display: block !important;
    -webkit-line-clamp: none !important;
    overflow: visible !important;
}
.no-print {
    display: none !important;
}
.report-print-card .score-calculation-panel {
    display: block !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
}
.report-print-card .score-calc-step1-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    display: grid !important;
}
.report-print-card .score-calc-step3-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    display: grid !important;
}
</style>
</head><body>${clone.outerHTML}
</body></html>`;

        return { fullHtml, pdfTitle };
    };

    const handleDownload = async () => {
        setDownloadError(null);
        setDownloading(true);
        setDownloadProgress(10);
        setDownloadStep(tr('download.preparing', 'Preparing report data...'));

        try {
            const { fullHtml, pdfTitle } = await generateFullyRenderedHTML();
            const filename = `${pdfTitle}.pdf`;

            setDownloadProgress(40);
            setDownloadStep(tr('download.generating', 'Generating PDF...'));

            // Call backend API to generate PDF
            const blob = await generatePDFFromHTML(fullHtml, sessionData?.id, filename, true);

            setDownloadProgress(80);
            setDownloadStep(tr('download.finalizing', 'Finalizing download...'));

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setDownloadProgress(100);
            setDownloadStep(tr('download.complete', 'Download complete!'));
        } catch (error) {
            console.error('PDF generation failed:', error);
            setDownloadError(error?.message || 'Failed to generate PDF');
        } finally {
            setTimeout(() => {
                setDownloading(false);
                setDownloadProgress(0);
                setDownloadStep('');
            }, 800);
        }
    };

    const handleSendEmailClick = async () => {
        if (isSendingEmail) return;
        try {
            const { fullHtml, pdfTitle } = await generateFullyRenderedHTML();
            const filename = `${pdfTitle}.pdf`;
            sendEmail?.(feedback, sessionData?.id, sessionData?.candidate?.email, `${sessionData?.candidate?.firstName || ''} ${sessionData?.candidate?.lastName || ''}`.trim() || sessionData?.candidate?.email, fullHtml, filename, true);
        } catch (err) {
            console.error('Email preparation failed:', err);
        }
    };

    if (loadingFeedback || loadCompleteFlash) {
        const loaderSteps = [
            {
                Icon: Server,
                label: tr('loading.stepBackend', 'Connecting to API'),
                sub: tr('loading.stepBackendSub', 'Reaching the assessment service')
            },
            {
                Icon: Database,
                label: tr('loading.stepDatabase', 'Loading session records'),
                sub: tr('loading.stepDatabaseSub', 'Fetching interview data from the database')
            },
            {
                Icon: Brain,
                label: tr('loading.stepAi', 'Preparing AI insights'),
                sub: tr('loading.stepAiSub', 'Assembling scores, Q&A, and narrative feedback')
            }
        ];
        const loaderWrapperClass = isPage
            ? 'min-h-screen bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4'
            : 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4';

        const showLoaderSuccess = loadCompleteFlash && !loadingFeedback;

        return (
            <div
                className={loaderWrapperClass}
                role="status"
                aria-live="polite"
                aria-busy={!showLoaderSuccess}
            >
                <div
                    className={`relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl bg-white transition-colors duration-300 ${showLoaderSuccess ? 'border-2 border-emerald-400/60' : 'border border-white/15'
                        }`}
                >
                    <div
                        className={`px-5 py-4 text-white transition-colors duration-300 ${showLoaderSuccess
                            ? 'bg-gradient-to-r from-emerald-500 to-green-600'
                            : 'bg-gradient-to-r from-cyan-500 to-blue-600'
                            }`}
                    >
                        <div className="flex justify-between items-start gap-3">
                            <div className="min-w-0">
                                <h2 className="text-lg font-bold leading-tight">
                                    AptiView – {reportTitle}
                                </h2>
                                <p
                                    className={`text-sm mt-1 transition-colors duration-300 ${showLoaderSuccess ? 'text-emerald-100/95' : 'text-cyan-100/95'
                                        }`}
                                >
                                    {reportSubtitle}
                                </p>
                            </div>
                            {onClose && (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="shrink-0 rounded-full p-2 bg-white/15 hover:bg-white/25 transition-colors"
                                    aria-label={tr('actions.close', 'Close')}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <div
                        className={`px-6 py-8 bg-gradient-to-b transition-colors duration-300 ${showLoaderSuccess ? 'from-emerald-50 to-white' : 'from-slate-50 to-white'
                            }`}
                    >
                        <div className="relative h-32 flex items-center justify-center mb-8">
                            <div
                                className={`absolute w-24 h-24 rounded-full animate-ping ${showLoaderSuccess
                                    ? 'bg-gradient-to-br from-emerald-400/40 to-green-600/35'
                                    : 'bg-gradient-to-br from-cyan-400/30 to-blue-600/30'
                                    }`}
                                style={{ animationDuration: showLoaderSuccess ? '1.4s' : '2.2s' }}
                            />
                            <div
                                className={`absolute w-[5.5rem] h-[5.5rem] rounded-full border-[3px] ${showLoaderSuccess
                                    ? 'border-emerald-100 border-t-emerald-600 animate-none'
                                    : 'border-cyan-100 border-t-cyan-600 animate-spin'
                                    }`}
                            />
                            <div
                                className={`absolute w-[4.5rem] h-[4.5rem] rounded-full border-[3px] ${showLoaderSuccess
                                    ? 'border-green-100 border-b-green-600 animate-none'
                                    : 'border-blue-100 border-b-blue-600 animate-spin [animation-direction:reverse] [animation-duration:1.8s]'
                                    }`}
                            />
                            <Brain
                                className={`relative w-11 h-11 drop-shadow-sm transition-colors duration-300 ${showLoaderSuccess ? 'text-emerald-700' : 'text-blue-700'
                                    }`}
                                aria-hidden
                            />
                        </div>

                        <div className="space-y-2.5">
                            {loaderSteps.map((step, stepIndex) => {
                                const { Icon } = step;
                                const isActive = !showLoaderSuccess && reportLoaderStep === stepIndex;
                                return (
                                    <div
                                        key={step.label}
                                        className={`flex items-center gap-3 rounded-xl px-3.5 py-3 transition-all duration-500 ${showLoaderSuccess
                                            ? 'bg-emerald-50 ring-2 ring-emerald-400/45 shadow-sm opacity-100'
                                            : isActive
                                                ? 'bg-cyan-50 ring-2 ring-cyan-400/50 shadow-sm scale-[1.02]'
                                                : 'bg-gray-50/80 opacity-65'
                                            }`}
                                    >
                                        <div
                                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-300 ${showLoaderSuccess
                                                ? 'bg-emerald-600 text-white'
                                                : isActive
                                                    ? 'bg-cyan-600 text-white'
                                                    : 'bg-gray-200 text-gray-500'
                                                }`}
                                        >
                                            <Icon className="w-5 h-5" aria-hidden />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p
                                                className={`font-semibold text-sm transition-colors ${showLoaderSuccess || isActive ? 'text-gray-900' : 'text-gray-600'
                                                    }`}
                                            >
                                                {step.label}
                                            </p>
                                            <p
                                                className={`text-xs leading-snug transition-colors ${showLoaderSuccess ? 'text-emerald-800/80' : 'text-gray-500'
                                                    }`}
                                            >
                                                {step.sub}
                                            </p>
                                        </div>
                                        {showLoaderSuccess ? (
                                            <CheckCircle className="w-5 h-5 shrink-0 text-emerald-600" strokeWidth={2.25} aria-hidden />
                                        ) : (
                                            isActive && (
                                                <span className="h-2 w-2 shrink-0 rounded-full bg-cyan-500 animate-pulse" />
                                            )
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <p
                            className={`text-center text-xs mt-6 leading-relaxed transition-colors duration-300 ${showLoaderSuccess ? 'text-emerald-800 font-medium' : 'text-gray-400'
                                }`}
                        >
                            {showLoaderSuccess
                                ? tr('loading.allJobsDoneLine', 'All jobs done — opening your report.')
                                : tr(
                                    'loading.waitForJson',
                                    'Loading complete report data from the server. This screen stays open until the response is fully received.'
                                )}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (feedbackError) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4">
                <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl text-center">
                    <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{tr('error.unableToLoad', 'Unable to Load Report')}</h3>
                    <p className="text-red-600 text-sm mb-4">{feedbackError}</p>
                    <button onClick={onClose} className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-semibold">{tr('actions.close', 'Close')}</button>
                </div>
            </div>
        );
    }

    if (!feedback) return null;

    const bothIdentityNull = !(feedback?.aadhaar_card || sessionData?.aadhaar_card) && !(feedback?.current_photo || sessionData?.current_photo);
    const coords = extractCoordinates();
    const locationText = extractLocationText();

    const wrapperClass = isPage
        ? 'min-h-screen bg-gray-50'
        : 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[999999] p-4';

    return (
        <div id="report-print-area" className={wrapperClass}>
            <div className={`report-print-card relative bg-white rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col border border-gray-200 ${isPage ? 'my-4' : 'max-h-[96vh]'}`}>
                {/* Header - outside contentRef for PDF */}
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-3 sm:p-4 flex-shrink-0">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div className="flex items-center gap-3">
                            {isPage && onClose && (
                                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors no-print" title="Back">
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                            )}
                            <div className="bg-white/10 rounded-lg p-2"><Brain className="w-5 h-5 sm:w-6 sm:h-6" /></div>
                            {companyLogo && <img src={companyLogo} alt="Company" className="h-7 sm:h-8 w-auto object-contain" onError={e => { e.target.style.display = 'none'; }} />}
                            <div>
                                <h1 className="text-lg sm:text-xl font-bold text-white">AptiView – {reportTitle}</h1>
                                <p className="text-gray-200 text-xs sm:text-sm">{reportSubtitle}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto no-print">
                            <button
                                onClick={handleDownload}
                                disabled={downloading}
                                title={downloading ? tr('actions.generating', 'Generating...') : tr('actions.downloadPdf', 'Download PDF')}
                                className="flex items-center justify-center p-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-500 text-white transition-colors"
                                aria-label={tr('actions.downloadPdf', 'Download PDF')}
                            >
                                {downloading ? <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : <Download className="w-5 h-5" />}
                            </button>
                            {role !== 'Candidate' && sendEmail != null && (
                                <button
                                    onClick={handleSendEmailClick}
                                    disabled={isSendingEmail}
                                    title={isSendingEmail ? tr('actions.sending', 'Sending...') : emailSent ? tr('actions.sent', 'Sent!') : tr('actions.sendEmail', 'Send Email')}
                                    className="flex items-center justify-center p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-70"
                                    aria-label={tr('actions.sendEmail', 'Send Email')}
                                >
                                    {isSendingEmail ? <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : <Mail className="w-5 h-5" />}
                                </button>
                            )}
                            {!isPage && onClose && (
                                <button onClick={onClose} className="p-2 rounded-lg bg-gray-500/90 hover:bg-gray-400 text-white transition-colors" aria-label="Close" title={tr('actions.close', 'Close')}><X className="w-5 h-5" /></button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Content - this ref is used for PDF */}
                <div className="flex-1 overflow-y-auto bg-gray-50 report-print-scroll">
                    <div ref={contentRef} className="p-4 sm:p-6 space-y-4 bg-white">
                        {/* Candidate Information - compact */}
                        <div className="rounded-xl p-4 sm:p-5 border border-blue-100 bg-gradient-to-br from-blue-50/50 to-white">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-lg"><User className="w-5 h-5 text-white" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">{tr('candidateInfo.title', 'Candidate Information')}</h2>
                                    <p className="text-gray-600 text-xs">{tr('candidateInfo.subtitle', 'Personal details and verification status')}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 CandidateInformationSection">
                                <div className="lg:col-span-1">
                                    {bothIdentityNull ? (
                                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">{tr('identityCard.notDisplayed', 'Identity card not displayed in this jurisdiction.')}</div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="text-center">
                                                <p className="text-gray-600 text-xs mb-1">{tr('candidateInfo.idVerification', 'ID Verification')}</p>
                                                {(feedback?.aadhaar_card || sessionData?.aadhaar_card) && (
                                                    isAadhaarPdf && aadhaarPdfUrl ? (
                                                        <div className="w-full h-28 sm:h-36 rounded-lg bg-gray-100 border overflow-hidden">
                                                            <object data={aadhaarPdfUrl} type="application/pdf" className="w-full h-full" />
                                                        </div>
                                                    ) : (
                                                        <div className="w-full h-24 rounded-lg bg-gray-100 border overflow-hidden cursor-pointer" onClick={() => setZoomModal({ isOpen: true, src: resolveMediaUrl(feedback?.aadhaar_card || sessionData?.aadhaar_card), alt: 'ID', isPdf: false, pdfUrl: null })}>
                                                            <img src={resolveMediaUrl(feedback?.aadhaar_card || sessionData?.aadhaar_card)} alt="ID" className="w-full h-full object-cover" />
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                            <div className="text-center">
                                                <p className="text-gray-600 text-xs mb-1">{tr('candidateInfo.livePhoto', 'Live Photo')}</p>
                                                <div className="w-full h-24 rounded-lg bg-gray-100 border overflow-hidden">
                                                    {(feedback?.current_photo || sessionData?.current_photo) ? (
                                                        <img src={resolveMediaUrl(feedback?.current_photo || sessionData?.current_photo)} alt="Live" className="w-full h-full object-cover" onClick={() => setZoomModal({ isOpen: true, src: resolveMediaUrl(feedback?.current_photo || sessionData?.current_photo), alt: 'Live', isPdf: false, pdfUrl: null })} />
                                                    ) : <Camera className="w-6 h-6 text-gray-400 mx-auto mt-8" />}
                                                </div>
                                            </div>
                                            {feedback?.detailed_breakdown?.verification?.similarity_score != null && (
                                                <div className="col-span-2 mt-2 text-center">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${feedback.detailed_breakdown?.verification?.status === 'passed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {feedback.detailed_breakdown?.verification?.status === 'passed' ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                                                        {tr('candidateInfo.similarity', 'Similarity')}: {feedback.detailed_breakdown?.verification?.similarity_score}%
                                                    </span>
                                                    {locationText && <div className="mt-1 text-xs text-gray-600 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />{locationText}</div>}
                                                    {coords && (
                                                        <a href={`https://www.openstreetmap.org/?mlat=${coords.latitude}&mlon=${coords.longitude}#map=15/${coords.latitude}/${coords.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs mt-1 inline-block">{tr('candidateInfo.viewLocation', 'View Location on Map')}</a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="lg:col-span-2">
                                    <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-3 py-1 rounded font-bold text-xs uppercase tracking-wide inline-block mb-2">APTI_{sessionData?.company}_{sessionData?.id}</div>
                                    <h3 className="text-lg font-bold text-gray-900 break-words">{sessionData?.candidate?.firstName} {sessionData?.candidate?.lastName}</h3>
                                    {sessionData?.job_description_title && <p className="text-gray-600 text-sm italic mb-3">{sessionData.job_description_title}</p>}
                                    {sessionData?.scheduled_by_name && (
                                        <div className="mb-3 inline-flex items-center gap-2.5 max-w-full">
                                            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-semibold text-white">
                                                {sessionData.scheduled_by_name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-indigo-700">{tr('candidateInfo.scheduledByLabel', 'Company Admin')}</span>
                                                <span className="block text-sm font-semibold text-gray-900 truncate">{sessionData.scheduled_by_name}</span>
                                            </span>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                        <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-blue-600 flex-shrink-0" /><span className="text-gray-700 truncate">{sessionData?.candidate?.email}</span></div>
                                        {sessionData?.candidate?.profile?.mobile && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-green-600 flex-shrink-0" /><span className="text-gray-700">{sessionData.candidate.profile.country_code} {sessionData.candidate.profile.mobile}</span></div>}
                                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-purple-600 flex-shrink-0" /><span className="text-gray-700">{sessionData?.started_at ? new Date(sessionData.started_at).toLocaleDateString(i18n.language || 'en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) : '—'}</span></div>
                                        <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-orange-600 flex-shrink-0" /><span className="text-gray-700">{calculateTime(interviewStartedAt, sessionData?.ended_at)}</span></div>
                                        <div className="flex items-center gap-2"><Building className="w-4 h-4 text-indigo-600 flex-shrink-0" /><span className="text-gray-700">{userDetails?.admin_companies?.[0] || tr('candidateInfo.individual', 'Individual')}</span></div>
                                        <div className="flex items-center gap-2"><Monitor className="w-4 h-4 text-blue-600 flex-shrink-0" /><span className="text-gray-700">{extractBrowserText() || tr('candidateInfo.browserMissing', 'Browser not detected')}</span></div>
                                    </div>
                                    {Array.isArray(sessionData?.tags) && sessionData.tags.length > 0 && (
                                        <div className="mt-3">
                                            <span className="text-gray-600 text-xs font-semibold">{tr('candidateInfo.tags', 'Tags')}</span>
                                            <div className="flex flex-wrap gap-1.5 mt-1">
                                                {sessionData.tags.map((tag, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs">{tag}</span>)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Performance Dashboard - compact */}
                        <div className="rounded-xl p-4 sm:p-5 border border-emerald-100 bg-gradient-to-br from-emerald-50/50 to-white">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-2 rounded-lg"><Award className="w-5 h-5 text-white" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">{tr('performance.title', 'Performance Dashboard')}</h2>
                                    <p className="text-gray-600 text-xs">{tr('performance.subtitle', 'Comprehensive analysis and scoring overview')}</p>
                                </div>
                            </div>
                            {/* Grid: Scorecard (left 2/3) + Stat boxes (right 1/3) */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 PerformanceDashboardSection items-stretch">
                                <div className="lg:col-span-2 bg-white rounded-lg p-4 sm:p-4.5 border border-gray-200">
                                    <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2.5">
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900">{tr('performance.overallScore', 'Overall Performance Score')}</h3>
                                            <p className="text-gray-500 text-xs mt-0.5">{tr('performance.benchmarkSubtitle', 'Evaluated against the {{min}}-question competency benchmark', { min: feedback?.detailed_breakdown?.minimum_questions_for_scoring ?? 10 })}</p>
                                        </div>
                                        <span className="text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                                            {tr('performance.officialScorecard', 'Official Scorecard')}
                                        </span>
                                    </div>
                                    <ScoreComparisonGuide
                                        effectiveScore={feedback?.detailed_breakdown?.effective_score}
                                        totalScore={feedback?.detailed_breakdown?.total_score ?? sessionData?.total_score}
                                        questionCount={feedback?.detailed_breakdown?.qa_pairs?.length || sessionData?.questions?.length || 0}
                                        minimumQuestions={feedback?.detailed_breakdown?.minimum_questions_for_scoring ?? 10}
                                        sessionData={sessionData}
                                        feedback={feedback}
                                    />
                                </div>
                                <div className="flex flex-col justify-between gap-3">
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex-1 flex flex-col justify-center">
                                        <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-emerald-600" /><span className="font-bold text-gray-900 text-xs">{tr('performance.duration', 'Duration')}</span></div>
                                        <div className="text-lg font-bold text-emerald-600">{calculateTime(interviewStartedAt, sessionData?.ended_at)}</div>
                                        <p className="text-emerald-700 text-xs">{tr('performance.interviewLength', 'Interview Length')}</p>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex-1 flex flex-col justify-center">
                                        <div className="flex items-center gap-2 mb-1"><MessageCircle className="w-4 h-4 text-blue-600" /><span className="font-bold text-gray-900 text-xs">{tr('performance.questions', 'Questions')}</span></div>
                                        <div className="text-lg font-bold text-blue-600">{feedback?.detailed_breakdown?.qa_pairs?.length || sessionData?.questions?.length || 0}</div>
                                        <p className="text-blue-700 text-xs">{tr('performance.totalAsked', 'Total Asked')}</p>
                                    </div>
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 flex-1 flex flex-col justify-center">
                                        <div className="flex items-center gap-2 mb-1"><Shield className="w-4 h-4 text-orange-600" /><span className="font-bold text-gray-900 text-xs">{tr('performance.security', 'Security')}</span></div>
                                        <div className="text-lg font-bold text-orange-600">{feedback?.detailed_breakdown?.cheating_flags?.length || 0}</div>
                                        <p className="text-orange-700 text-xs">{tr('performance.alertsDetected', 'Alerts Detected')}</p>
                                        {(() => { const pct = computeAlertPercentage(sessionData, feedback); const { label } = getAlertPenalty(pct, tr); return <span className="text-xs font-semibold text-gray-600 mt-0.5 block">{label}</span>; })()}
                                    </div>
                                    {sessionData?.language_detail && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex-1 flex flex-col justify-center">
                                            <div className="flex items-center gap-2 mb-1"><Globe className="w-4 h-4 text-blue-600" /><span className="font-bold text-gray-900 text-xs">{tr('performance.language', 'Language')}</span></div>
                                            <div className="text-xs font-semibold text-blue-700">
                                                {(() => {
                                                    const langCode = (sessionData.language_detail.code || sessionData.language_detail.whisper_code || '').toLowerCase();
                                                    return t(`languages.${langCode}`, { defaultValue: sessionData.language_detail.name });
                                                })()}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            {/* Score explanation — full width, outside the grid so it doesn't stretch right column */}
                            <ScoreCalculationExplainer
                                effectiveScore={feedback?.detailed_breakdown?.effective_score}
                                totalScore={feedback?.detailed_breakdown?.total_score ?? sessionData?.total_score}
                                questionCount={feedback?.detailed_breakdown?.qa_pairs?.length || sessionData?.questions?.length || 0}
                                minimumQuestions={feedback?.detailed_breakdown?.minimum_questions_for_scoring ?? 10}
                                sessionData={sessionData}
                                feedback={feedback}
                            />
                        </div>

                        {/* Executive Summary - compact */}
                        <div className="pdf-page-break-before rounded-xl p-4 border border-orange-100 bg-gradient-to-br from-orange-50/50 to-white">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="bg-gradient-to-r from-orange-600 to-amber-600 p-2 rounded-lg"><FileText className="w-5 h-5 text-white" /></div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-900">{tr('summary.title', 'Executive Summary')}</h2>
                                    <p className="text-gray-600 text-xs">{tr('summary.subtitle', 'AI-generated insights and recommendations')}</p>
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-gray-200 text-sm text-gray-700 space-y-2">
                                {feedback?.summary?.split('\n').filter(Boolean).map((line, i) => (
                                    line.includes(':') && line.endsWith(':') ? <h4 key={i} className="font-bold text-gray-900 mt-2">{translateSummaryHeader(line.replace(':', ''), tr)}</h4> :
                                        line.startsWith('•') ? <p key={i} className="pl-3 border-l-2 border-gray-300">{line.substring(2)}</p> :
                                            <p key={i} className="leading-relaxed">{line}</p>
                                ))}
                            </div>
                        </div>

                        {feedback?.detailed_breakdown?.benchmarks && (
                            <BenchmarkScorecard benchmarks={feedback.detailed_breakdown.benchmarks} />
                        )}

                        {/* Skills Assessment - compact */}
                        {feedback?.detailed_breakdown?.categories && Object.keys(feedback.detailed_breakdown.categories).length > 0 && (
                            <div className="pdf-page-break-before rounded-xl p-4 border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-2 rounded-lg"><Target className="w-5 h-5 text-white" /></div>
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-900">{tr('skills.title', 'Skills Assessment')}</h2>
                                            <p className="text-gray-600 text-xs">{tr('skills.subtitle', 'Technical competency breakdown by category')}</p>
                                        </div>
                                    </div>
                                    <span className="text-purple-600 font-bold text-sm">{tr('skills.count', '{{count}} Skills', { count: Object.keys(feedback.detailed_breakdown.categories).length })}</span>
                                </div>
                                <ProfessionalSkillChart categories={feedback.detailed_breakdown.categories} />
                            </div>
                        )}

                        {/* Q&A - compact */}
                        <div className="pdf-page-break-before rounded-xl p-4 border border-cyan-100 bg-gradient-to-br from-cyan-50/50 to-white">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                                <div className="flex items-center gap-2">
                                    <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-2 rounded-lg"><MessageCircle className="w-5 h-5 text-white" /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">{tr('qaSection.title', 'Detailed Q&A Analysis')}</h2>
                                        <p className="text-gray-600 text-xs">{tr('qaSection.subtitle', 'Individual question performance with security monitoring')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-cyan-600 font-bold text-sm">{(feedback?.detailed_breakdown?.qa_pairs?.length || sessionData?.questions?.length || 0)} {tr('qaSection.questions', 'Questions')}</span>
                                    {sessionData?.session_video_url && (
                                        <a
                                            href={(() => {
                                                const raw = sessionData.session_video_url;
                                                try {
                                                    const u = new URL(raw, window.location.origin);
                                                    if (!u.searchParams.has('v')) u.searchParams.set('v', '2');
                                                    return u.toString();
                                                } catch {
                                                    return raw.includes('?') ? raw : `${raw}?v=2`;
                                                }
                                            })()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90"
                                        >
                                            <PlayCircle className="w-4 h-4" /><span>{tr('qaSection.fullRecording', 'Full Interview Recording')}</span><ExternalLink className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                                {(feedback?.detailed_breakdown?.qa_pairs?.length ? feedback.detailed_breakdown.qa_pairs : sessionData?.questions || []).map((qa, idx) => (
                                    <ProfessionalQACard key={idx} qa={qa?.question ? { ...qa, question: qa.question } : qa} index={idx} sessionData={sessionData} feedback={feedback} setZoomModal={setZoomModal} setVideoModal={setVideoModal} />
                                ))}
                            </div>
                        </div>

                        {/* How interview ended — same section language as Device & Connection Quality */}
                        <div className="pdf-page-break-before">
                            <SessionIntegrityPanel sessionData={sessionData} />
                        </div>

                        {/* Health Diagnostic Section - Full Width */}
                        <TelemetryChart sessionId={sessionData?.id} />

                        {/* Security Summary - compact */}
                        {feedback?.detailed_breakdown?.cheating_flags?.length > 0 && (
                            <div className="rounded-xl p-4 border border-red-200 bg-gradient-to-br from-red-50/50 to-white">
                                <div className="flex items-center justify-between mb-3">

                                    <div className="flex items-center gap-2">
                                        <div className="bg-gradient-to-r from-red-600 to-pink-600 p-2 rounded-lg">
                                            <Shield className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-900">
                                                {tr('securitySummary.title', 'Security Monitoring Summary')}
                                            </h2>
                                            <p className="text-gray-600 text-xs">
                                                {tr('securitySummary.subtitle', 'Automated monitoring detected security alerts')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Alert Badge with Red Dot */}
                                    <span className="bg-white rounded-full px-3 py-1 border-2 border-red-200 text-red-600 font-bold text-sm flex items-center gap-2">

                                        {/* Solid Red Dot */}
                                        <span className="w-2.5 h-2.5 bg-red-500 rounded-full"></span>

                                        {feedback?.detailed_breakdown?.cheating_flags?.length || 0} Alerts
                                    </span>

                                </div>
                            </div>
                        )}

                        {/* Plagiarism - compact */}
                        {feedback?.detailed_breakdown?.plagiarism_summary && (
                            <div className="rounded-xl p-4 border border-yellow-200 bg-gradient-to-br from-yellow-50/50 to-white">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-2 rounded-lg"><Eye className="w-5 h-5 text-white" /></div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">{tr('plagiarism.title', 'Plagiarism Analysis')}</h2>
                                        <p className="text-gray-600 text-xs">{tr('plagiarism.subtitle', 'Content originality assessment')}</p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="bg-white rounded p-2 border border-yellow-200"><span className="text-lg font-bold text-yellow-600">{feedback.detailed_breakdown.plagiarism_summary.total_questions}</span><p className="text-yellow-700 text-xs">{tr('plagiarism.totalQuestions', 'Total Questions')}</p></div>
                                    <div className="bg-white rounded p-2 border border-yellow-200"><span className="text-lg font-bold text-red-600">{feedback.detailed_breakdown.plagiarism_summary.plagiarism_yes}</span><p className="text-red-700 text-xs">{tr('plagiarism.flaggedResponses', 'Flagged')}</p></div>
                                    <div className="bg-white rounded p-2 border border-yellow-200"><span className="text-lg font-bold text-green-600">{feedback.detailed_breakdown.plagiarism_summary.plagiarism_no}</span><p className="text-green-700 text-xs">{tr('plagiarism.originalResponses', 'Original')}</p></div>
                                </div>
                            </div>
                        )}

                        {/* Microphone Validation section */}
                        <MicValidationSection
                            micTestAudio={feedback?.mic_test_audio || sessionData?.mic_test_audio}
                            micTestMetadata={feedback?.mic_test_metadata || sessionData?.mic_test_metadata}
                        />

                        {/* Identity Verification - compact */}
                        {feedback?.detailed_breakdown?.verification && (feedback?.aadhaar_card || sessionData?.aadhaar_card || feedback?.current_photo || sessionData?.current_photo) && (
                            <div className={`rounded-xl p-4 border-2 ${feedback.detailed_breakdown.verification.status === 'passed' ? 'border-green-200 bg-green-50/50' : 'border-yellow-200 bg-yellow-50/50'}`}>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Shield className={`w-5 h-5 ${feedback.detailed_breakdown.verification.status === 'passed' ? 'text-green-600' : 'text-yellow-600'}`} />
                                        <div>
                                            <h3 className="font-bold text-gray-900">{tr('verification.title', 'Identity Verification')}</h3>
                                            <p className="text-gray-600 text-xs">{tr('verification.subtitle', 'Face similarity between ID and photo')}</p>
                                        </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full font-bold text-sm ${feedback.detailed_breakdown.verification.status === 'passed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {feedback.detailed_breakdown.verification.similarity_score != null ? `${feedback.detailed_breakdown.verification.similarity_score}%` : tr('verification.na', 'N/A')}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Footer */}
                        <div className="text-center py-6 border-t border-gray-200">
                            <p className="text-gray-7 00 text-xs">
                                {tr('footer.generatedBy', 'This report was generated using Aptiview.ai.')}
                            </p>

                            {(userDetails?.email || userDetails?.firstName || userDetails?.lastName || userDetails?.username || userDetails?.first_name || userDetails?.last_name || userDetails?.admin_companies?.[0]) && (
                                <p className="text-blue-900 text-xs">
                                    {tr('footer.generatedByUser', 'Generated by {{name}} ({{email}}){{company}}', {
                                        name: [userDetails?.firstName || userDetails?.first_name, userDetails?.lastName || userDetails?.last_name].filter(Boolean).join(' ') || userDetails?.username || userDetails?.email || '—',
                                        email: userDetails?.email || '—',
                                        company: userDetails?.admin_companies?.[0] ? ` - ${userDetails.admin_companies[0]}` : ''
                                    })}
                                </p>
                            )}
                            <p className="text-gray-700 text-xs">
                                {tr('footer.generatedOnLabel', 'Report generated on')}{' '}
                                <span className="text-blue-900">{new Date().toLocaleDateString()}</span>
                                {' '}{tr('footer.generatedOnAt', 'at')}{' '}
                                <span className="text-blue-900">{new Date().toLocaleTimeString()}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {downloading && (
                    <div className="no-print absolute inset-0 bg-black/25 backdrop-blur-sm flex items-center justify-center z-[100] rounded-2xl p-4 overflow-hidden text-center font-poppins">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full border border-gray-100 animate-in fade-in zoom-in duration-300 relative overflow-hidden">
                            {/* Simplified decorative circles for interactive feel */}
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-blue-50/50 rounded-full blur-2xl animate-pulse"></div>
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-indigo-50/50 rounded-full blur-xl animate-pulse delay-700"></div>

                            <div className="flex flex-col items-center relative z-10">
                                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-4 rounded-full shadow-lg mb-6 animate-bounce">
                                    <Download className="w-8 h-8 text-white" />
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-2 whitespace-nowrap">{tr('download.title', 'Preparing Your Report')}</h3>

                                <div className="flex items-center gap-2 bg-blue-50/50 px-4 py-3 rounded-full border border-blue-100 shadow-sm transition-all duration-500 font-poppins">
                                    <div className="w-2 h-2 rounded-full bg-cyan-500 animate-ping"></div>
                                    <p className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">
                                        {downloadStep || tr('download.processing', 'Processing...')}
                                    </p>
                                </div>

                                <p className="mt-10 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] animate-pulse font-poppins">
                                    {tr('download.almostDone', 'Almost ready')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                {/* Download Error Modal */}
                {downloadError && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl text-center">
                            <XCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                            <h3 className="font-bold text-gray-800 mb-2">{tr('download.failed', 'Download Failed')}</h3>
                            <p className="text-red-600 text-sm mb-4">{downloadError}</p>
                            <div className="flex gap-2">
                                <button onClick={() => setDownloadError(null)} className="flex-1 bg-gray-600 text-white py-2 rounded-lg font-semibold text-sm">{tr('actions.close', 'Close')}</button>
                                <button onClick={() => { setDownloadError(null); handleDownload(); }} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-semibold text-sm">{tr('download.tryAgain', 'Try Again')}</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="no-print"><ZoomModal isOpen={zoomModal.isOpen} onClose={() => setZoomModal({ isOpen: false, src: null, alt: '', isPdf: false, pdfUrl: null })} src={zoomModal.src} alt={zoomModal.alt} isPdf={zoomModal.isPdf} pdfUrl={zoomModal.pdfUrl} /></div>

                {videoModal.isOpen && (
                    <div className="no-print fixed inset-0 bg-black/80 backdrop-blur-md z-[999] flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl max-w-3xl w-full overflow-hidden shadow-2xl border border-gray-100 flex flex-col">
                            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    <PlayCircle className="w-5 h-5 text-blue-600" />
                                    <h3 className="font-bold text-gray-900 text-base">{videoModal.title}</h3>
                                </div>
                                <button
                                    onClick={() => setVideoModal({ isOpen: false, url: null, title: '', startSeconds: undefined })}
                                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                                    aria-label={tr('actions.close', 'Close')}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="bg-black p-3 flex items-center justify-center">
                                <video
                                    key={`${videoModal.url}-${videoModal.startSeconds ?? 0}`}
                                    controls
                                    autoPlay
                                    src={videoModal.url}
                                    className="w-full max-h-[75vh] rounded-lg"
                                    onLoadedMetadata={(e) => {
                                        const start = videoModal.startSeconds;
                                        if (start != null && Number.isFinite(Number(start))) {
                                            e.currentTarget.currentTime = Number(start);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
